import { useCallback, useEffect, useRef } from "react";
import ZoomToolNav from "../zoomToolNav";
import { useAppSelector, useAppDispatch } from "../../redux/hooks";
import { batch } from "react-redux";
import {
  setUpdateFlag,
  setExpandIDs,
  setHighlightIDs,
  setTransform,
  handlePropertyBox,
  setStreamID,
  delStreamID,
} from "../../redux/actions/property";
import {
  setZoomMode,
  downloadSvgButtonClicked,
} from "../../redux/actions/layout";
import {
  setProduceDiagramFlag,
  showLoadingMask,
  hideLoadingMask,
} from "../../redux/actions/global";
import * as d3 from "d3";
import { ReactComponent as DownloadSvg } from "../../assets/icons/download.svg";
import { ReactComponent as SettingsSvg } from "../../assets/icons/settings.svg";
import { ReactComponent as PlusSvg } from "../../assets/icons/plus.svg";
import { ReactComponent as MinusSvg } from "../../assets/icons/minus.svg";
import "./diagram.css";
import { useUpdateEffect } from "react-use";
import type { Edge, HighlightIDs, Node } from "../../redux/reducer/property";
import { SetRequired } from "type-fest";

const saveSvgAsPng = require("save-svg-as-png");
const highlightNodeTypes: string[] = [
  "inner join",
  "left outer join",
  "right outer join",
  "full outer join",
  "cross join",
  "pivot",
  "unpivot",
  "union",
  "union all",
  "except",
  "intersect",
  "minus",
  "Semi Join (Exists)",
  "Anti Join (Not Exists)",
  "logical",
];
const Diagram = () => {
  const dispatch = useAppDispatch();

  const user = useAppSelector((state) => state.propertyReducer.user);
  const currentDiagram = useAppSelector(
    (state) => state.propertyReducer.userLayout[user].currentDiagram
  );
  const activeDiagram = useAppSelector(
    (state) =>
      state.propertyReducer.userLayout[user].diagramList[currentDiagram]
  );

  const chartData = useAppSelector(
    (state) =>
      state.propertyReducer.userLayout[user].diagramList[currentDiagram]
        .diagramData
  );

  const sqlData = useAppSelector(
    (state) =>
      state.propertyReducer.userLayout[user].diagramList[currentDiagram].sqlData
  );

  const searchNodes = useAppSelector(
    (state) =>
      state.propertyReducer.userLayout[user].diagramList[currentDiagram]
        .searchNodes
  );
  const propertyBox = useAppSelector(
    (state) =>
      state.propertyReducer.userLayout[user].diagramList[currentDiagram]
        .propertyBox
  );
  const updateFlag = useAppSelector(
    (state) => state.propertyReducer.userLayout[user].updateFlag
  );
  const highlightIDs = useAppSelector(
    (state) => state.propertyReducer.userLayout[user].highlightIDs
  );
  const highlightNodes = Object.keys(highlightIDs);
  const showColumnState = useAppSelector(
    (state) => state.propertyReducer.userLayout[user].showColumnState
  );
  const isShowColumnButtonClicked = useAppSelector(
    (state) => state.propertyReducer.userLayout[user].isShowColumnButtonClicked
  );
  const actpropertyBoxState = useAppSelector(
    (state) =>
      state.propertyReducer.userLayout[user].diagramList[currentDiagram]
        .propertyBox
  );
  const activeQueryModified = useAppSelector(
    (state) =>
      state.propertyReducer.userLayout[user].queryModified[currentDiagram]
  );
  const zoomState = useAppSelector((state) => state.layoutReducer.zoomState);
  const isHighlighted = useAppSelector(
    (state) =>
      state.propertyReducer.userLayout[user].diagramList[currentDiagram]
        .isHighlighted
  );
  const isZoomButtonClicked = useAppSelector(
    (state) => state.layoutReducer.isZoomButtonClicked
  );
  const isDownloadSvgClicked = useAppSelector(
    (state) => state.layoutReducer.isDownloadSvgClicked
  );
  const zoomMode = useAppSelector((state) => state.layoutReducer.zoomMode);
  let streamID = useAppSelector(
    (state) =>
      state.propertyReducer.userLayout[user].diagramList[currentDiagram]
        .streamID
  );
  streamID = activeDiagram ? streamID : [];
  let cTitleHeight =
    useAppSelector(
      (state) =>
        state.propertyReducer.userLayout[user].diagramList[currentDiagram]
          .defaultHeight
    ) || 40;
  cTitleHeight = activeDiagram ? cTitleHeight : 40;
  var mainTransform = useAppSelector(
    (state) =>
      state.propertyReducer.userLayout[user].diagramList[currentDiagram]
        .transform
  );

  const svgRef = useRef<SVGSVGElement>(null);
  const diagramRef = useRef<SVGGElement>(null);

  const cCharWidth = 8;
  const cCharHeight = 16;

  // variable of diagram size
  var minX, minY, maxX, maxY, dataWidth, dataHeight;
  // draw diagram
  const drawDiagram = () => {
    if (!diagramRef.current) {
      console.error("cannot draw diagram before it is mounted");
      return;
    }
    if (!chartData?.nodes || !chartData?.edges) {
      console.error("cannot draw diagram as node or edge data is missing");
      return;
    }
    const container = d3.select(diagramRef.current);
    if (chartData && Object.keys(chartData).length > 0) {
      dispatch(setHighlightIDs({}));
      let nodeData = container.selectAll<SVGGElement, Node>(".node").data();
      if (nodeData.length > 0 && updateFlag && chartData.nodes) {
        chartData.nodes.forEach((node) => {
          for (let i = 0; i < nodeData.length; i++) {
            if (nodeData[i].id === node.id) {
              node.hidden = nodeData[i].hidden;
              node.stream_expanded = nodeData[i].stream_expanded;
              break;
            }
          }
        });

        let edgeData = container.selectAll<SVGGElement, Edge>(".edge").data();
        chartData.edges.forEach((edge) => {
          for (let i = 0; i < edgeData.length; i++) {
            if (edgeData[i].id === edge.id) {
              edge.hidden = edgeData[i].hidden;
              break;
            }
          }
        });

        update();
      } else {
        chartData.nodes.forEach((node) => {
          node.hidden = false;
          node.stream_expanded = true;
        });
        chartData.edges.forEach((edge) => {
          edge.hidden = false;
        });
        handleStreamDiagram();

        create();
      }
    } else {
      clean();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  };

  const shouldHighlight = (d: Node) => {
    const subtype = d.subtype?.toLowerCase() ?? "";
    return highlightNodeTypes.includes(subtype);
  };

  useUpdateEffect(() => {
    if (!diagramRef.current || !chartData?.nodes || !chartData?.edges) return;

    drawDiagram();
  }, [currentDiagram, chartData]);

  useEffect(() => {
    if (chartData && Object.keys(chartData).length) {
      if (!updateFlag) {
        clean();
      }
      createDefs();
      createElements();
      setZoomEvent();
    } else {
      clean();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sqlData, isHighlighted, currentDiagram]);

  useUpdateEffect(() => {
    let nodes = document.querySelectorAll(".node");
    nodes.forEach((node) => {
      const datasetHeader = node.querySelector(".frame.dataset-header");
      if (activeQueryModified) {
        node.classList.add("disabled");
        if (datasetHeader) {
          datasetHeader.classList.add("disabled");
        }
      } else {
        node.classList.remove("disabled");
        if (datasetHeader) {
          datasetHeader.classList.remove("disabled");
        }
      }
    });
  }, [activeQueryModified]);

  // handle column show / hide for all nodes
  useUpdateEffect(() => {
    if (
      chartData &&
      chartData.nodes &&
      chartData.width &&
      chartData.width > 0
    ) {
      let expand_IDs: string[] = [];
      if (showColumnState === "showAll") {
        chartData["nodes"].forEach((node) => {
          expand_IDs.push(node.id);
          node.collapse = false;
        });
      }
      if (showColumnState === "hideAll") {
        chartData["nodes"].forEach((node) => {
          node.collapse = true;
        });
      }
      batch(() => {
        dispatch(setUpdateFlag(true));
        dispatch(setExpandIDs(expand_IDs));
        dispatch(setProduceDiagramFlag());
      });
    }
  }, [showColumnState, isShowColumnButtonClicked]);

  // set zoom
  useUpdateEffect(() => {
    if (chartData && chartData.width && chartData.width > 0) {
      setZoomRate(zoomState);
    }
  }, [zoomState, isZoomButtonClicked]);

  // highlight node in diagram, tablelist, sql text
  const handleClickedNode = useCallback(
    (event: React.MouseEvent<SVGGElement>, d: Node) => {
      if (isHighlighted === sqlData) {
        event.stopPropagation();
        if (
          Array.from((event.target as SVGGElement).classList).includes(
            "disabled"
          )
        ) {
          return;
        }

        if (d.subtype?.toLowerCase() === "logical" || d.type === "operation") {
          if (shouldHighlight(d)) {
            highlightInlineNodes(d);
          }
        } else {
          // check if table is already highlighted.

          let ids: HighlightIDs = {};
          ids = { [d.id]: [d.id] };
          if (d.subtype === "CTE" && d.sameAs) {
            d.sameAs.forEach((id) => {
              ids?.[d.id]?.push(id);
            });
          }
          dispatch(setHighlightIDs(ids));
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, isHighlighted, sqlData, highlightIDs.length, highlightIDs]
  );

  // set highlight and tooltip for operator
  useUpdateEffect(() => {
    let nodes = document.querySelectorAll(".node.highlighted");
    nodes.forEach((node) => {
      node.classList.remove("highlighted");
    });

    const operator = document.querySelectorAll(".notify");
    operator.forEach((node) => {
      const element = node.querySelector("g");
      if (element) {
        element.setAttribute("style", "display: none");
        element.setAttribute("display", "none");
      }
    });
    highlightNodes.forEach((key) => {
      const node = document.getElementById(key)?.querySelector("use");
      if (node && node.classList.contains("operator")) {
        d3.select("#notify_" + key).attr("style", "display: block");
        d3.select("#notify_" + key).attr("display", "block");
      }

      highlightIDs?.[key].forEach((id) => {
        const target = document.getElementById(id);
        if (target) {
          target.classList.add("highlighted");
        }
      });
    });

    // check highlighted node is in visible area.
    if (!diagramRef.current) {
      throw new Error(
        "Cannot target highlighted node because diagram is not mounted"
      );
    }
    const container = diagramRef.current;
    const nodeData = d3
      .select<SVGGElement, unknown>(container)
      .selectAll<SVGGElement, Node>(".node.highlighted")
      .data();
    let region = getNodesBound(nodeData);
    if (!region) return;

    if (!svgRef.current) {
      throw new Error("Trying to dereference svgRef before it's mounted");
    }

    let mainWidth = svgRef.current.clientWidth;
    let mainHeight = svgRef.current.clientHeight;
    if (
      !mainTransform ||
      ((region.minX + mainTransform.x) * mainTransform.k >= 5 &&
        (region.maxX + mainTransform.x) * mainTransform.k <= mainWidth - 5 &&
        (region.minY + mainTransform.y) * mainTransform.k >= 5 &&
        (region.maxY + mainTransform.y) * mainTransform.k <= mainHeight - 5)
    ) {
      return;
    }

    // centeralize highlighted nodes.
    let scale = Math.max(
      ((region.maxX - region.minX) * mainTransform.k) / mainWidth,
      ((region.maxY - region.minY) * mainTransform.k) / mainHeight
    );

    if (scale < 1) {
      scale = mainTransform.k;
    } else {
      scale = (mainTransform.k / scale) * 0.8;
    }

    let offsetX = (mainWidth - (region.maxX + region.minX) * scale) / 2;
    let offsetY = (mainHeight - (region.maxY + region.minY) * scale) / 2;

    const elemSVG = d3.select<SVGGElement, unknown>(svgRef.current);
    elemSVG
      .transition()
      .duration(750)
      .call(
        zoom.transform,
        d3.zoomIdentity.translate(offsetX, offsetY).scale(scale)
      );
  }, [highlightNodes?.length]);

  useUpdateEffect(() => {
    if (!searchNodes || searchNodes.length === 0) {
      return;
    }

    if (!svgRef.current || diagramRef.current) {
      console.error(
        "Cannot center highlighted nodes before application is fully mounted"
      );
      return;
    }

    // check highlighted node is in visible area.
    let region = getNodesBound(searchNodes);
    if (!region) return;

    let mainWidth = svgRef.current.clientWidth;
    let mainHeight = svgRef.current.clientHeight;
    if (
      !mainTransform ||
      ((region.minX + mainTransform.x) * mainTransform.k >= 5 &&
        (region.maxX + mainTransform.x) * mainTransform.k <= mainWidth - 5 &&
        (region.minY + mainTransform.y) * mainTransform.k >= 5 &&
        (region.maxY + mainTransform.y) * mainTransform.k <= mainHeight - 5)
    ) {
      return;
    }

    // centeralize highlighted nodes.
    let scale = Math.max(
      ((region.maxX - region.minX) * mainTransform.k) / mainWidth,
      ((region.maxY - region.minY) * mainTransform.k) / mainHeight
    );

    if (scale < 1) {
      scale = mainTransform.k;
    } else {
      scale = (mainTransform.k / scale) * 0.8;
    }

    let offsetX = (mainWidth - (region.maxX + region.minX) * scale) / 2;
    let offsetY = (mainHeight - (region.maxY + region.minY) * scale) / 2;

    const elemSVG = d3.select<SVGGElement, unknown>(svgRef.current);

    elemSVG
      .transition()
      .duration(750)
      .call(
        zoom.transform,
        d3.zoomIdentity.translate(offsetX, offsetY).scale(scale)
      );
  }, [searchNodes]);

  // download svg to image
  useUpdateEffect(() => {
    if (!diagramRef.current) {
      throw new Error("Cannot download svg because diagram is not mounted");
    }

    const container = d3.select<SVGGElement, unknown>(diagramRef.current);

    if (container.selectAll(".node").data().length === 0) return;
    dispatch(showLoadingMask());

    saveSvgAsPng
      .saveSvgAsPng(
        document.querySelector(".diagram-svg"),
        currentDiagram + ".png",
        {
          backgroundColor: "white",
          scale: mainTransform ? 1 / mainTransform.k : 1,
          encoderOptions: 1,
        }
      )
      .then(() => {
        dispatch(hideLoadingMask());
      });
  }, [isDownloadSvgClicked]);

  // show property box
  const handleClickedShowProperyBox = () => {
    batch(() => {
      dispatch(setHighlightIDs({}));
      dispatch(handlePropertyBox(true));
    });
  };

  const handleClickedZoomIn = () => {
    if (!diagramRef.current || !svgRef.current) {
      throw new Error("Cannot zoom in because diagram is not mounted");
    }
    const container = d3.select<SVGGElement, unknown>(diagramRef.current);
    const elemSVG = d3.select<SVGGElement, unknown>(svgRef.current);

    if (container.selectAll(".node").data().length === 0) return;
    zoom.scaleBy(elemSVG.transition().duration(750), 2);
    // elemSVG.transition().duration(750).call(zoom.scaleBy, 2);
  };

  const handleClickedZoomOut = () => {
    if (!diagramRef.current || !svgRef.current) {
      throw new Error("Cannot zoom out because diagram is not mounted");
    }
    const container = d3.select<SVGGElement, unknown>(diagramRef.current);
    const elemSVG = d3.select<SVGGElement, unknown>(svgRef.current);

    if (container.selectAll(".node").data().length === 0) return;
    zoom.scaleBy(elemSVG.transition().duration(750), 0.5);
  };

  // download svg
  const downloadSvgToPng = () => {
    dispatch(downloadSvgButtonClicked());
  };

  // get node from node array using id
  const getNode = (nodeData: Node[], id: string) => {
    return nodeData.find((node) => node.id === id);
  };

  // create diagram using data
  const create = () => {
    clean();
    createDefs();
    createElements();
    setZoomEvent();
    setZoomRate();
  };

  // update diagram using data
  const update = () => {
    if (!diagramRef.current) {
      throw new Error(
        "trying to update diagram but it is not mounted, probably a leak"
      );
    }
    if (!chartData) {
      throw new Error("trying to update diagram but chartData is undefined");
    }
    if (!chartData.edges || !chartData.nodes) {
      throw new Error(
        "Cannot update diagram as edges or nodes are not defined"
      );
    }

    const container = d3.select(diagramRef.current);

    // move nodes
    container
      .selectAll(".node")
      .data(chartData["nodes"])
      .transition()
      .duration(100)
      .attr("transform", (d) => "translate(" + d.x + "," + d.y + ")");

    // transform dataset
    container
      .selectAll(".dataset")
      .data(chartData["nodes"])
      .transition()
      .duration(300)
      .attr("width", (d) => d.width)
      .attr("height", (d) => d.height)
      .attr("transform", (d) => {
        if (!d.width || !d.height) {
          return "";
        } else {
          const translateWidth = (d.width * -1) / 2;
          const translateHeight = (d.height * -1) / 2;
          return "translate(" + translateWidth + "," + translateHeight + ")";
        }
      });

    // transform node
    container
      .selectAll(".dataset-body")
      .data(chartData["nodes"])
      .transition()
      .duration(300)
      .attr("width", (d) => (d.width ? d.width : null))
      .attr("height", (d) => {
        let staticHeight = 43;
        if (d.refdb !== null) {
          staticHeight = staticHeight + 30;
        }
        if (d.refsch !== null) {
          staticHeight = staticHeight + 30;
        }
        if (d.collapse) {
          //setting default height
          return 40;
        } else {
          //25 is one item height
          return d.columns
            ? staticHeight + 25 * d.columns.length
            : staticHeight + 25;
        }
      });

    // transform header
    container
      .selectAll(".dataset-header")
      .data(chartData["nodes"])
      .transition()
      .duration(300)
      .attr("d", (d) =>
        makeHeaderRect(
          0.5,
          0.5,
          d.width - 2,
          cTitleHeight - 1,
          10,
          d.columns !== undefined && d.columns.length > 0 && !d.collapse
        )
      );

    container
      .selectAll(".dataset-icon-box")
      .data(chartData["nodes"])
      .transition()
      .duration(300)
      .attr("d", (d) =>
        makeIconRect(
          0.5,
          0.5,
          cTitleHeight - 1,
          cTitleHeight - 1,
          10,
          d.columns !== undefined && d.columns.length > 0 && !d.collapse
        )
      );

    // transform detail view
    container
      .selectAll(".dataset-detail-view")
      .data(
        chartData["nodes"].filter(
          (d) => d.columns !== undefined && d.columns.length > 0
        )
      )
      .attr(
        "class",
        (d) => "dataset-detail-view" + (d.collapse ? " fade" : "")
      );

    container
      .selectAll(".detail-wrapper")
      .data(
        chartData["nodes"].filter(
          (d) => d.columns !== undefined && d.columns.length > 0
        )
      )
      .attr("width", (d) => d.width)
      .attr("height", (d) => {
        if (d.collapse) {
          return 0;
        } else {
          //25 is one item height
          return 25 * (d.columns?.length || 0);
        }
      })
      .attr("class", (d) => "detail-wrapper" + (d.collapse ? " hidden" : ""));

    container
      .selectAll(".detail-split-line")
      .data(
        chartData["nodes"].filter(
          (d) => d.columns !== undefined && d.columns.length > 0
        )
      )
      .attr("d", (d) => makeDetailSplitLines(d, true));

    container
      .selectAll(".detail-column")
      .data(
        chartData["nodes"].filter(
          (d) => d.columns !== undefined && d.columns.length > 0
        )
      )
      .attr("width", (d) => d.width - 5)
      .attr("height", (d) => {
        if (d.collapse) {
          return 0;
        } else {
          //25 is one item height
          return 25 * (d.columns?.length || 0);
        }
      });

    // move edges
    container
      .selectAll(".path")
      .data(chartData["edges"])
      .transition()
      .duration(250)
      .attr("d", (d: Edge) => line(makeEdge(d)));

    // move recursilve edges
    container
      .selectAll(".recursive-path")
      .data(chartData["nodes"].filter((d) => d.recursiveCteTargetId))
      .transition()
      .duration(300)
      .attr("d", (d) =>
        line(makeRecursiveEdge(d as SetRequired<Node, "recursiveCteTargetId">))
      );

    // move tooltip
    container
      .selectAll(".notify")
      .data(chartData["nodes"])
      .transition()
      .duration(300)
      .attr("transform", (d) => "translate(" + d.x + "," + d.y + ")");

    // move collpase handle box
    container
      .selectAll(".node .collapse-handle")
      .data(chartData["nodes"].filter((d) => canHaveCollapseHandle(d)))
      .transition()
      .duration(300)
      .attr("transform", (d) => calcCollapseHandlePosition(d));

    container
      .selectAll(".node .handle-connector")
      .data(chartData["nodes"].filter((d) => canHaveCollapseHandle(d)))
      .transition()
      .duration(300)
      .attr("x2", activeDiagram.direction === "LR" ? 45 : 14)
      .attr("y2", activeDiagram.direction === "LR" ? 0 : 30);

    // move detail view handle box
    container
      .selectAll(".node .detailview-handle")
      .data(chartData["nodes"].filter((d) => canHaveDetailViewHandle(d)))
      .transition()
      .duration(300)
      .attr("transform", (d) => calcDetailViewHandlePosition(d));

    container
      .selectAll(".node .detailview-handle .arrow")
      .data(chartData["nodes"].filter((d) => canHaveDetailViewHandle(d)))
      .attr("xlink:href", (d) => {
        return d.collapse ? "#expand_down" : "#collapse_up";
      });

    // move operator
    container
      .selectAll(".operator")
      .data(chartData["nodes"].filter((d) => d.type === "operation"))
      .transition()
      .duration(300)
      .attr(
        "transform",
        "translate(-" +
          (activeDiagram.operatorRadius || 15) +
          ",-" +
          (activeDiagram.operatorRadius || 15) +
          ") scale(" +
          ((activeDiagram.operatorRadius || 15) / 17 || 0) +
          ")"
      );

    container
      .selectAll(".operator-label")
      .data(chartData["nodes"].filter((d) => d.type === "operation"))
      .transition()
      .duration(300)
      .attr("y", (activeDiagram.operatorRadius || 15) + 20);

    // refresh all nodes and edges
    refresh();

    // zoom if mode auto
    if (zoomMode === "auto") {
      setZoomRate();
    }
  };

  // Clear svg content before adding new elements
  const clean = () => {
    if (!diagramRef.current) {
      throw new Error("Cannot clean because diagram is not yet mounted");
    }
    const container = d3.select<SVGGElement, unknown>(diagramRef.current);

    container.selectAll("*").remove();
  };

  // correspond table from label to link
  const linkMap = {
    physical: "#physical_table",
    logical: "#inline_table",
    select: "#select",
    sourceless: "#pseudo_table",
    recursive: "#recursive_table",
    functional: "#function_table",
    udtf: "#function_table",
    cte: "#common_table",
    union: "#union",
    "union all": "#union_all",
    intersect: "#intersect",
    minus: "#minus",
    except: "#except",
    pivot: "#pivot",
    unpivot: "#unpivot",
    filter: "#filter",
    having: "#having",
    qualify: "#qualify",
    sample: "#sample",
    limit: "#limit",
    top: "#top",
    fetch: "#fetch",
    join: "#join",
    "left outer join": "#left_join",
    "right outer join": "#right_join",
    "full outer join": "#full_join",
    "cross join": "#cross_join",
    "inner join": "#inner_join",
    "sort asc": "#asc_sort",
    "sort desc": "#desc_sort",
    "sort mixed": "#both_sort",
    group: "#group",
    "Semi Join (Exists)": "#semi_join",
    "Anti Join (Not Exists)": "#anti_join",
  };

  // apply scale
  const zoom = d3
    .zoom<SVGGElement, unknown>()
    .filter((event) => {
      return (
        !event.target.classList.contains("haddle-near") &&
        !event.target.classList.contains("detail-column-text")
      );
    })
    .extent([
      [0, 0],
      [1000, 1000],
    ])
    .on("zoom", (event: d3.D3ZoomEvent<SVGGElement, unknown>) => {
      if (!svgRef.current || !diagramRef.current) return;
      const container = d3.select<SVGGElement, unknown>(diagramRef.current);

      if (container.selectAll(".node").data().length === 0) return;

      mainTransform = event.transform;
      dispatch(setTransform(mainTransform));
      container.attr(
        "transform",
        "translate(" +
          (event.transform.x || 0) +
          "," +
          (event.transform.y || 0) +
          ") scale(" +
          (event.transform.k || 0) +
          ")"
      );

      updateScaleNotify(event.transform.k);
    })
    .on("start", (event) => {
      if (
        mainTransform &&
        mainTransform.k === event.transform.k &&
        svgRef.current
      ) {
        svgRef.current.style.cursor = `move`;
      }
    })
    .on("end", (_) => {
      if (!svgRef.current) return;
      svgRef.current.style.cursor = `unset`;
    });

  // make curve from edge points
  const line = d3
    .line()
    .x((d) => d[0])
    .y((d) => d[1])
    .curve(d3.curveBasis);

  const makeEdge = (d: Edge): [number, number][] => {
    let uniquePoints: Edge["points"] = [];
    if (d.points.length === 0) return [];

    let points = [...d.points];
    let targetNode = getNode(chartData?.nodes || [], d.target);
    if (targetNode && canHaveCollapseHandle(targetNode)) {
      let point = d.points[d.points.length - 1];
      let offset = Math.min(60, activeDiagram.layerSpan || 60);
      if (activeDiagram.direction === "LR") {
        points.push({ x: point.x - offset, y: point.y });
      } else {
        points.push({ x: point.x, y: point.y - offset });
      }
    }

    points.forEach((p) => {
      let include = false;
      for (let i = 0; i < uniquePoints.length; i++) {
        if (uniquePoints[i].x === p.x && uniquePoints[i].y === p.y) {
          include = true;
          break;
        }
      }

      if (!include) {
        uniquePoints.push(p);
      }
    });

    uniquePoints.sort((p1, p2) => {
      if (activeDiagram.direction === "LR") {
        return p1.x - p2.x;
      } else {
        return p1.y - p2.y;
      }
    });

    return Array.from(uniquePoints.values()).map((point) => [point.x, point.y]);
  };

  // make recursive edge
  const makeRecursiveEdge = (
    d: SetRequired<Node, "recursiveCteTargetId">
  ): [number, number][] => {
    let points: Edge["points"] = [];
    if (!chartData || d.recursiveCteTargetId === null) {
      return [];
    }
    let targetNode = chartData.nodes
      ? getNode(chartData.nodes, d.recursiveCteTargetId)
      : undefined;
    if (targetNode) {
      if (activeDiagram.direction === "LR") {
        points.push({ x: d.x, y: d.y + d.height / 2 });
        points.push({ x: d.x, y: d.y + d.height / 2 + 60 });
        points.push({
          x: targetNode.x,
          y: targetNode.y + targetNode.height / 2 + 60,
        });
        points.push({
          x: targetNode.x,
          y: targetNode.y + targetNode.height / 2,
        });
      } else {
        points.push({ x: d.x + d.width / 2, y: d.y });
        points.push({ x: d.x + d.width / 2 + 60, y: d.y });
        points.push({
          x: targetNode.x + targetNode.width / 2 + 60,
          y: targetNode.y,
        });
        points.push({
          x: targetNode.x + targetNode.width / 2,
          y: targetNode.y,
        });
      }
    }

    return Array.from(points.values()).map((point) => [point.x, point.y]);
  };

  // set Zoom event handler
  const setZoomEvent = () => {
    if (!svgRef.current) {
      throw new Error("cannot set zoom event because svg is not yet mounted");
    }
    const elemSVG = d3.select<SVGGElement, unknown>(svgRef.current);
    elemSVG.call(zoom);
  };

  const makeIconRect = (
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number,
    expanded: boolean
  ) => {
    let str =
      "M" +
      x +
      "," +
      (y + radius) +
      "a" +
      radius +
      "," +
      -radius +
      " 0 0 1 " +
      radius +
      "," +
      -radius +
      "h" +
      (width - radius) +
      "v" +
      height;

    if (expanded) {
      str += "h" + -width;
    } else {
      str +=
        "h" +
        (radius - width) +
        "a" +
        -radius +
        "," +
        -radius +
        " 0 0 1 " +
        -radius +
        "," +
        -radius;
    }

    str += "z";
    return str;
  };

  const makeHeaderRect = (
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number,
    expanded: boolean
  ) => {
    let str =
      "M" +
      x +
      "," +
      (y + radius) +
      "a" +
      radius +
      "," +
      -radius +
      " 0 0 1 " +
      radius +
      "," +
      -radius +
      "h" +
      (width - radius * 2) +
      "a" +
      -radius +
      "," +
      radius +
      " 0 0 1 " +
      radius +
      "," +
      radius;

    if (expanded) {
      str += "v" + (height - radius) + "h" + -width;
    } else {
      str +=
        "v" +
        (height - radius * 2) +
        "a" +
        -radius +
        "," +
        -radius +
        " 0 0 1 " +
        -radius +
        "," +
        radius +
        "h" +
        (radius * 2 - width) +
        "a" +
        -radius +
        "," +
        -radius +
        " 0 0 1 " +
        -radius +
        "," +
        -radius;
    }

    str += "z";
    return str;
  };

  const getDiagramSize = () => {
    if (!diagramRef.current) {
      throw new Error(
        "Cannot getDiagramSize because the diagram is not mounted"
      );
    }
    const container = d3.select<SVGGElement, unknown>(diagramRef.current);

    let bound = getNodesBound(
      container
        .selectAll<SVGGElement, Node>(".node")
        .data()
        .filter((d) => !d.hidden)
    );
    if (!bound) {
      return;
    }

    minX = bound.minX;
    minY = bound.minY;
    maxX = bound.maxX;
    maxY = bound.maxY;
    dataWidth = maxX - minX;
    dataHeight = maxY - minY;
    return {
      minX,
      minY,
      maxX,
      maxY,
      dataWidth,
      dataHeight,
    };
  };

  const getNodesBound = (nodes: typeof searchNodes) => {
    if (!nodes || nodes.length === 0) return false;

    let _minX = nodes[0].x - nodes[0].width / 2;
    let _minY = nodes[0].y;
    let _maxX = nodes[0].x + nodes[0].width / 2;
    let _maxY = nodes[0].y + nodes[0].height;
    nodes.forEach((node) => {
      if (_minX > node.x - node.width / 2) _minX = node.x - node.width / 2;
      if (_minY > node.y) _minY = node.y;
      if (_maxX < node.x + node.width / 2) _maxX = node.x + node.width / 2;
      if (_maxY < node.y + node.height) _maxY = node.y + node.height;
    });

    return {
      minX: _minX,
      minY: _minY,
      maxX: _maxX,
      maxY: _maxY,
    };
  };

  // set zoom scale
  const setZoomRate = (action = "normal") => {
    if (!svgRef.current) {
      console.error("Cannot setZoomRate because the SVG is not mounted");
      return;
    }

    const elemSVG = d3.select<SVGGElement, unknown>(svgRef.current);
    const diagramSize = getDiagramSize();
    let mainWidth = svgRef.current.clientWidth;
    let mainHeight = svgRef.current.clientHeight;

    if (!diagramSize) {
      elemSVG
        .transition()
        .duration(750)
        .call(
          zoom.transform,
          d3.zoomIdentity.translate(mainWidth / 2, mainHeight / 2).scale(1)
        );
      return;
    }

    let scale = 1;
    let offsetX = (mainWidth - (diagramSize.maxX + diagramSize.minX)) / 2;
    let offsetY = (mainHeight - (diagramSize.maxY + diagramSize.minY)) / 2;

    // move chart to center
    switch (action) {
      case "original": {
        scale = 1;
        break;
      }
      case "normal":
      case "fit": {
        scale = Math.min(
          (mainWidth - 50) / diagramSize.dataWidth,
          (mainHeight - 50) / diagramSize.dataHeight
        );
        if (action === "normal") {
          if (scale > 1) scale = 1;
        }
        offsetX =
          (mainWidth - (diagramSize.maxX + diagramSize.minX) * scale) / 2;
        offsetY =
          (mainHeight - (diagramSize.maxY + diagramSize.minY) * scale) / 2;
        break;
      }
      case "keep":
        scale = mainTransform ? mainTransform.k : 1;
        offsetX = mainTransform ? mainTransform.x : 0;
        offsetY = mainTransform ? mainTransform.y : 0;
        break;
      default:
        break;
    }

    elemSVG
      .transition()
      .duration(750)
      .call(
        zoom.transform,
        d3.zoomIdentity.translate(offsetX, offsetY).scale(scale)
      );
  };

  // create defs
  const createDefs = () => {
    if (!diagramRef.current) {
      throw new Error("Cannot createDefs beause diagram is not mounted");
    }
    const container = d3.select<SVGGElement, unknown>(diagramRef.current);

    let defs = container.append("defs");
    defs
      .append("marker")
      .attr("id", "edgeEndmarker")
      .attr("viewBox", "0 0 5 6")
      .attr("refX", 4)
      .attr("refY", 3)
      .attr("markerUnits", "strokeWidth")
      .attr("markerWidth", 5)
      .attr("markerHeight", 6)
      .attr("orient", "auto")
      .append("path")
      .attr(
        "d",
        "M6.17621e-08 5.64676C3.45808e-08 5.80222 0.169594 5.89824 0.302899 5.81826L4.71417 3.1715C4.84364 3.09382 4.84364 2.90618 4.71417 2.8285L0.3029 0.181739C0.169596 0.101756 1.01449e-06 0.197778 9.87311e-07 0.353237L6.17621e-08 5.64676Z"
      )
      .attr("fill", "#6bb2d5");

    defs
      .append("marker")
      .attr("id", "recursiveEdgeEndmarker")
      .attr("viewBox", "0 0 5 6")
      .attr("refX", 4)
      .attr("refY", 3)
      .attr("markerUnits", "strokeWidth")
      .attr("markerWidth", 5)
      .attr("markerHeight", 6)
      .attr("orient", "auto")
      .append("path")
      .attr(
        "d",
        "M6.17621e-08 5.64676C3.45808e-08 5.80222 0.169594 5.89824 0.302899 5.81826L4.71417 3.1715C4.84364 3.09382 4.84364 2.90618 4.71417 2.8285L0.3029 0.181739C0.169596 0.101756 1.01449e-06 0.197778 9.87311e-07 0.353237L6.17621e-08 5.64676Z"
      )
      .attr("fill", "#810029");

    // expand column
    let expand_down = defs
      .append("g")
      .attr("id", "expand_down")
      .attr("viewBox", "0 0 8 7")
      .attr("width", 8)
      .attr("height", 7);
    expand_down
      .append("path")
      .attr(
        "d",
        "M3.99664 3.02492L1.48223 0.746338L1.00005 1.05754L3.9966 3.77387L7 1.05754L6.51782 0.746338L3.99664 3.02492Z"
      )
      .attr("strokeWidth", 0.3)
      .attr("strokeLinejoin", "round");
    expand_down
      .append("path")
      .attr(
        "d",
        "M3.99664 5.251L1.48223 2.97241L1.00005 3.28361L3.9966 5.99995L7 3.28361L6.51782 2.97241L3.99664 5.251Z"
      )
      .attr("strokeWidth", 0.3)
      .attr("strokeLinejoin", "round");

    let collapse_up = defs
      .append("g")
      .attr("id", "collapse_up")
      .attr("viewBox", "0 0 8 7")
      .attr("width", 8)
      .attr("height", 7);
    collapse_up
      .append("path")
      .attr(
        "d",
        "M4.00336 3.72142L6.51777 6L6.99995 5.6888L4.0034 2.97247L1 5.6888L1.48218 6L4.00336 3.72142Z"
      )
      .attr("strokeWidth", 0.3)
      .attr("strokeLinejoin", "round");
    collapse_up
      .append("path")
      .attr(
        "d",
        "M4.00336 1.49528L6.51777 3.77386L6.99995 3.46266L4.0034 0.74633L1 3.46266L1.48218 3.77386L4.00336 1.49528Z"
      )
      .attr("strokeWidth", 0.3)
      .attr("strokeLinejoin", "round");
    let up_stream, down_stream;

    // up stream
    if (activeDiagram.direction === "LR") {
      down_stream = defs
        .append("g")
        .attr("class", "down_stream")
        .attr("id", "down_stream")
        .attr("viewBox", "0 0 14 14")
        .attr("width", 14)
        .attr("height", 14);
      down_stream
        .append("path")
        .attr(
          "d",
          "M0.5 7C0.5 3.41015 3.41015 0.5 7 0.5C10.5898 0.499999 13.5 3.41015 13.5 7C13.5 10.5899 10.5899 13.5 7 13.5C3.41015 13.5 0.500001 10.5899 0.5 7Z"
        )
        .attr("fill", "#3F74F6")
        .attr("stroke", "#3F74F6");
      down_stream
        .append("path")
        .attr(
          "d",
          "M7.11884 6.99664L9.94115 4.48223L9.55569 4.00005L6.19118 6.9966L9.55569 10L9.94115 9.51782L7.11884 6.99664Z"
        )
        .attr("fill", "white")
        .attr("strokeWidth", 0.3)
        .attr("stroke", "white");
      down_stream
        .append("path")
        .attr(
          "d",
          "M4.36151 6.99664L7.18381 4.48223L6.79836 4.00005L3.43385 6.9966L6.79836 10L7.18381 9.51782L4.36151 6.99664Z"
        )
        .attr("fill", "white")
        .attr("strokeWidth", 0.3)
        .attr("stroke", "white");

      up_stream = defs
        .append("g")
        .attr("class", "up_stream")
        .attr("id", "up_stream")
        .attr("viewBox", "0 0 14 14")
        .attr("width", 14)
        .attr("height", 14);
      up_stream
        .append("path")
        .attr(
          "d",
          "M0.5 7C0.5 3.41015 3.41015 0.5 7 0.5C10.5898 0.499999 13.5 3.41015 13.5 7C13.5 10.5899 10.5899 13.5 7 13.5C3.41015 13.5 0.500001 10.5899 0.5 7Z"
        )
        .attr("fill", "#3F74F6")
        .attr("stroke", "#3F74F6");
      up_stream
        .append("path")
        .attr(
          "d",
          "M7.11884 6.99664L9.94115 4.48223L9.55569 4.00005L6.19118 6.9966L9.55569 10L9.94115 9.51782L7.11884 6.99664Z"
        )
        .attr("fill", "white")
        .attr("strokeWidth", 0.3)
        .attr("stroke", "white");
      up_stream
        .append("path")
        .attr(
          "d",
          "M4.36151 6.99664L7.18381 4.48223L6.79836 4.00005L3.43385 6.9966L6.79836 10L7.18381 9.51782L4.36151 6.99664Z"
        )
        .attr("fill", "white")
        .attr("strokeWidth", 0.3)
        .attr("stroke", "white");
    } else {
      up_stream = defs
        .append("g")
        .attr("class", "rotate-90")
        .attr("transform", "translate(-15px,0px) scale(2)")
        .attr("id", "up_stream")
        .attr("viewBox", "0 0 14 14")
        .attr("width", 14)
        .attr("height", 14);
      up_stream
        .append("path")
        .attr(
          "d",
          "M0.5 7C0.5 3.41015 3.41015 0.5 7 0.5C10.5898 0.499999 13.5 3.41015 13.5 7C13.5 10.5899 10.5899 13.5 7 13.5C3.41015 13.5 0.500001 10.5899 0.5 7Z"
        )
        .attr("fill", "#3F74F6")
        .attr("stroke", "#3F74F6");
      up_stream
        .append("path")
        .attr(
          "d",
          "M7.11884 6.99664L9.94115 4.48223L9.55569 4.00005L6.19118 6.9966L9.55569 10L9.94115 9.51782L7.11884 6.99664Z"
        )
        .attr("fill", "white")
        .attr("strokeWidth", 0.3)
        .attr("stroke", "white");
      up_stream
        .append("path")
        .attr(
          "d",
          "M4.36151 6.99664L7.18381 4.48223L6.79836 4.00005L3.43385 6.9966L6.79836 10L7.18381 9.51782L4.36151 6.99664Z"
        )
        .attr("fill", "white")
        .attr("strokeWidth", 0.3)
        .attr("stroke", "white");

      down_stream = defs
        .append("g")
        .attr("class", "rotate90")
        .attr("transform", "translate(-15px,-28px) scale(2)")
        .attr("id", "down_stream")
        .attr("viewBox", "0 0 14 14")
        .attr("width", 14)
        .attr("height", 14);
      down_stream
        .append("path")
        .attr(
          "d",
          "M0.5 7C0.5 3.41015 3.41015 0.5 7 0.5C10.5898 0.499999 13.5 3.41015 13.5 7C13.5 10.5899 10.5899 13.5 7 13.5C3.41015 13.5 0.500001 10.5899 0.5 7Z"
        )
        .attr("fill", "#3F74F6")
        .attr("stroke", "#3F74F6");
      down_stream
        .append("path")
        .attr(
          "d",
          "M7.11884 6.99664L9.94115 4.48223L9.55569 4.00005L6.19118 6.9966L9.55569 10L9.94115 9.51782L7.11884 6.99664Z"
        )
        .attr("fill", "white")
        .attr("strokeWidth", 0.3)
        .attr("stroke", "white");
      down_stream
        .append("path")
        .attr(
          "d",
          "M4.36151 6.99664L7.18381 4.48223L6.79836 4.00005L3.43385 6.9966L6.79836 10L7.18381 9.51782L4.36151 6.99664Z"
        )
        .attr("fill", "white")
        .attr("strokeWidth", 0.3)
        .attr("stroke", "white");
    }

    //////// operators ///////
    // union
    let union = defs
      .append("g")
      .attr("id", "union")
      .attr("viewBox", "0 0 35 35")
      .attr("width", 35)
      .attr("height", 35);
    union
      .append("rect")
      .attr("x", 1)
      .attr("y", 1)
      .attr("width", 33)
      .attr("height", 33)
      .attr("rx", 16.5)
      .attr("fill", "white");
    union
      .append("path")
      .attr(
        "d",
        "M9.34654 13.5385H14.5385V8.34654C14.5385 8.15516 14.696 8 14.8848 8H26.6536C26.8448 8 27 8.15762 27 8.3464V20.1152C27 20.3065 26.8402 20.4615 26.6535 20.4615H21.4615V25.6535C21.4615 25.8448 21.304 26 21.1152 26H9.3464C9.15516 26 9 25.8424 9 25.6536V13.8848C9 13.6935 9.1598 13.5385 9.34654 13.5385ZM14.8846 14.2307C15.0759 14.2307 15.231 14.0709 15.231 13.8843V8.69236H26.3081V19.7695H21.1161C20.9247 19.7695 20.7697 19.9293 20.7697 20.1159V25.3079H9.69258V14.2307H14.8846ZM21.1157 16.3077C21.307 16.3077 21.4619 16.4559 21.4619 16.6571V18.0353C21.4619 18.2283 21.3013 18.3846 21.1157 18.3846C20.9245 18.3846 20.7696 18.2365 20.7696 18.0353V16.6571C20.7696 16.4641 20.9302 16.3077 21.1157 16.3077ZM18.3433 14.2307H16.9651C16.7721 14.2307 16.6158 14.0702 16.6158 13.8846C16.6158 13.6934 16.7638 13.5385 16.9651 13.5385H18.3433C18.5362 13.5385 18.6927 13.699 18.6927 13.8846C18.6927 14.0759 18.5445 14.2307 18.3433 14.2307ZM21.1157 14.9231C20.9245 14.9231 20.7696 14.7626 20.7696 14.577V14.2307H20.4234C20.2323 14.2307 20.0773 14.0702 20.0773 13.8846C20.0773 13.6934 20.2255 13.5385 20.4267 13.5385H21.1126C21.3055 13.5385 21.4619 13.6866 21.4619 13.8878V14.5737C21.4619 14.7666 21.3013 14.9231 21.1157 14.9231ZM14.8849 17.6924C14.6937 17.6924 14.5388 17.5442 14.5388 17.343V15.9648C14.5388 15.7718 14.6993 15.6155 14.8849 15.6155C15.0762 15.6155 15.231 15.7635 15.231 15.9648V17.343C15.231 17.5359 15.0705 17.6924 14.8849 17.6924ZM17.6574 19.7693H19.0356C19.2286 19.7693 19.3849 19.9299 19.3849 20.1154C19.3849 20.3067 19.2368 20.4616 19.0356 20.4616H17.6574C17.4644 20.4616 17.308 20.301 17.308 20.1154C17.308 19.9242 17.4562 19.7693 17.6574 19.7693ZM14.8849 19.077C15.0762 19.077 15.231 19.2375 15.231 19.4231V19.7693H15.5773C15.7684 19.7693 15.9234 19.9299 15.9234 20.1154C15.9234 20.3067 15.7752 20.4616 15.574 20.4616H14.8881C14.6951 20.4616 14.5388 20.3135 14.5388 20.1123V19.4264C14.5388 19.2334 14.6993 19.077 14.8849 19.077Z"
      )
      .attr("fill-rule", "evenodd")
      .attr("clip-rule", "evenodd");

    let union_all = defs
      .append("g")
      .attr("id", "union_all")
      .attr("viewBox", "0 0 35 35")
      .attr("width", 35)
      .attr("height", 35);
    union_all
      .append("rect")
      .attr("x", 1)
      .attr("y", 1)
      .attr("width", 33)
      .attr("height", 33)
      .attr("rx", 16.5)
      .attr("fill", "white");
    union_all
      .append("path")
      .attr(
        "d",
        "M14.2187 13.8462H20.7815C20.9872 13.8462 21.1538 14.0126 21.1538 14.2186V20.7814C21.1538 20.9871 20.9875 21.1538 20.7815 21.1538H14.2187C14.013 21.1538 13.8463 20.9874 13.8463 20.7814V14.2186C13.8463 14.0129 14.0127 13.8462 14.2187 13.8462ZM20.4233 14.5769V20.4233H14.5769V14.5769H20.4233ZM14.2116 10.9231C14.4135 10.9231 14.577 11.0795 14.577 11.2919V12.0158C14.577 12.2195 14.4075 12.3845 14.2116 12.3845C14.0098 12.3845 13.8463 12.2283 13.8463 12.0158V11.2919C13.8463 11.0882 14.0157 10.9231 14.2116 10.9231ZM26.6345 10.9231C26.8364 10.9231 27 11.0795 27 11.2919V12.0158C27 12.2195 26.8304 12.3845 26.6345 12.3845C26.4328 12.3845 26.2692 12.2283 26.2692 12.0158V11.2919C26.2692 11.0882 26.4386 10.9231 26.6345 10.9231ZM26.6345 13.8462C26.8364 13.8462 27 14.0025 27 14.2149V14.9388C27 15.1425 26.8304 15.3077 26.6345 15.3077C26.4328 15.3077 26.2692 15.1512 26.2692 14.9388V14.2149C26.2692 14.0112 26.4386 13.8462 26.6345 13.8462ZM26.6345 16.7692C26.8364 16.7692 27 16.9256 27 17.138V17.862C27 18.0657 26.8304 18.2308 26.6345 18.2308C26.4328 18.2308 26.2692 18.0744 26.2692 17.862V17.138C26.2692 16.9343 26.4386 16.7692 26.6345 16.7692ZM20.7885 22.6156C20.9904 22.6156 21.1539 22.7719 21.1539 22.9843V23.7082C21.1539 23.9119 20.9844 24.0771 20.7885 24.0771C20.5867 24.0771 20.4232 23.9207 20.4232 23.7082V22.9843C20.4232 22.7806 20.5926 22.6156 20.7885 22.6156ZM17.862 8.73082H17.1381C16.9344 8.73082 16.7693 8.56138 16.7693 8.36549C16.7693 8.16363 16.9257 8 17.1381 8H17.862C18.0657 8 18.2309 8.16959 18.2309 8.36549C18.2309 8.56719 18.0745 8.73082 17.862 8.73082ZM20.7852 8.73082H20.0612C19.8575 8.73082 19.6924 8.56138 19.6924 8.36549C19.6924 8.16363 19.8488 8 20.0612 8H20.7852C20.9888 8 21.1539 8.16959 21.1539 8.36549C21.1539 8.56719 20.9976 8.73082 20.7852 8.73082ZM23.7081 8.73082H22.9842C22.7805 8.73082 22.6155 8.56138 22.6155 8.36549C22.6155 8.16363 22.7718 8 22.9842 8H23.7081C23.9118 8 24.077 8.16959 24.077 8.36549C24.077 8.56719 23.9205 8.73082 23.7081 8.73082ZM14.2116 9.46164C14.4134 9.46164 14.5769 9.29205 14.5769 9.09616V8.73082H14.9424C15.1441 8.73082 15.3077 8.56139 15.3077 8.36549C15.3077 8.16363 15.1513 8 14.9388 8H14.2149C14.0112 8 13.8462 8.15644 13.8462 8.36886V9.09279C13.8462 9.29648 14.0157 9.46164 14.2116 9.46164ZM26.6344 9.46164C26.4327 9.46164 26.2691 9.29205 26.2691 9.09616V8.73082H25.9038C25.7019 8.73082 25.5383 8.56139 25.5383 8.36549C25.5383 8.16363 25.6947 8 25.9071 8H26.6311C26.8348 8 26.9999 8.15644 26.9999 8.36886V9.09279C26.9999 9.29648 26.8303 9.46164 26.6344 9.46164ZM26.6344 19.6923C26.4327 19.6923 26.2691 19.8618 26.2691 20.0576V20.4231H25.9038C25.7019 20.4231 25.5383 20.5926 25.5383 20.7885C25.5383 20.9903 25.6947 21.1538 25.9071 21.1538H26.6311C26.8348 21.1538 26.9999 20.9975 26.9999 20.7851V20.0612C26.9999 19.8575 26.8303 19.6923 26.6344 19.6923ZM20.7884 25.5384C20.5866 25.5384 20.4231 25.7079 20.4231 25.9038V26.2692H20.0576C19.8559 26.2692 19.6923 26.4386 19.6923 26.6345C19.6923 26.8364 19.8487 27 20.0612 27H20.7851C20.9888 27 21.1538 26.8435 21.1538 26.6311V25.9072C21.1538 25.7035 20.9843 25.5384 20.7884 25.5384ZM8.36556 25.5384C8.56726 25.5384 8.73089 25.7079 8.73089 25.9038V26.2692H9.09622C9.29808 26.2692 9.46171 26.4386 9.46171 26.6345C9.46171 26.8364 9.30527 27 9.09286 27H8.36893C8.16524 27 8.00008 26.8435 8.00008 26.6311V25.9072C8.00008 25.7035 8.16967 25.5384 8.36556 25.5384ZM8.36556 16.7691C8.56726 16.7691 8.73089 16.9255 8.73089 17.1379V17.8619C8.73089 18.0656 8.56145 18.2307 8.36556 18.2307C8.16371 18.2307 8.00008 18.0743 8.00008 17.8619V17.1379C8.00008 16.9343 8.16967 16.7691 8.36556 16.7691ZM8.36556 19.6922C8.56726 19.6922 8.73089 19.8487 8.73089 20.0611V20.785C8.73089 20.9887 8.56145 21.1537 8.36556 21.1537C8.16371 21.1537 8.00008 20.9974 8.00008 20.785V20.0611C8.00008 19.8574 8.16967 19.6922 8.36556 19.6922ZM8.36556 22.6154C8.56726 22.6154 8.73089 22.7717 8.73089 22.9841V23.708C8.73089 23.9117 8.56145 24.0769 8.36556 24.0769C8.16371 24.0769 8.00008 23.9204 8.00008 23.708V22.9841C8.00008 22.7804 8.16967 22.6154 8.36556 22.6154ZM12.016 14.5766H11.292C11.0884 14.5766 10.9232 14.4072 10.9232 14.2113C10.9232 14.0094 11.0796 13.846 11.292 13.846H12.016C12.2197 13.846 12.3847 14.0154 12.3847 14.2113C12.3847 14.4131 12.2284 14.5766 12.016 14.5766ZM12.016 27H11.292C11.0884 27 10.9232 26.8304 10.9232 26.6345C10.9232 26.4328 11.0796 26.2692 11.292 26.2692H12.016C12.2197 26.2692 12.3847 26.4386 12.3847 26.6345C12.3847 26.8364 12.2284 27 12.016 27ZM14.9389 27H14.215C14.0113 27 13.8463 26.8304 13.8463 26.6345C13.8463 26.4328 14.0026 26.2692 14.215 26.2692H14.9389C15.1426 26.2692 15.3078 26.4386 15.3078 26.6345C15.3078 26.8364 15.1513 27 14.9389 27ZM17.862 27H17.1381C16.9344 27 16.7693 26.8304 16.7693 26.6345C16.7693 26.4328 16.9257 26.2692 17.1381 26.2692H17.862C18.0657 26.2692 18.2309 26.4386 18.2309 26.6345C18.2309 26.8364 18.0745 27 17.862 27ZM23.708 21.1536H22.9841C22.7804 21.1536 22.6154 20.9841 22.6154 20.7882C22.6154 20.5864 22.7717 20.4229 22.9841 20.4229H23.708C23.9117 20.4229 24.0769 20.5923 24.0769 20.7882C24.0769 20.9901 23.9204 21.1536 23.708 21.1536ZM8.36548 15.3075C8.56719 15.3075 8.73081 15.1381 8.73081 14.9422V14.5767H9.09614C9.298 14.5767 9.46163 14.4073 9.46163 14.2114C9.46163 14.0095 9.30519 13.846 9.09278 13.846H8.36885C8.16516 13.846 8 14.0023 8 14.2147V14.9387C8 15.1424 8.16959 15.3075 8.36548 15.3075Z"
      )
      .attr("fill-rule", "evenodd")
      .attr("clip-rule", "evenodd");
    union_all
      .append("rect")
      .attr("x", 14)
      .attr("y", 14)
      .attr("width", 7)
      .attr("height", 7);
    union_all
      .append("rect")
      .attr("x", 15)
      .attr("y", 9)
      .attr("width", 11)
      .attr("height", 11);
    union_all
      .append("rect")
      .attr("x", 9)
      .attr("y", 15)
      .attr("width", 11)
      .attr("height", 11);

    let intersect = defs
      .append("g")
      .attr("id", "intersect")
      .attr("viewBox", "0 0 35 35")
      .attr("width", 35)
      .attr("height", 35);
    intersect
      .append("rect")
      .attr("x", 1)
      .attr("y", 1)
      .attr("width", 33)
      .attr("height", 33)
      .attr("rx", 16.5)
      .attr("fill", "white");
    intersect
      .append("path")
      .attr(
        "d",
        "M14.2187 13.8462H20.7815C20.9872 13.8462 21.1538 14.0126 21.1538 14.2186V20.7814C21.1538 20.9871 20.9875 21.1538 20.7815 21.1538H14.2187C14.013 21.1538 13.8463 20.9874 13.8463 20.7814V14.2186C13.8463 14.0129 14.0127 13.8462 14.2187 13.8462ZM20.4233 14.5769V20.4233H14.5769V14.5769H20.4233ZM14.2116 10.9231C14.4135 10.9231 14.577 11.0795 14.577 11.2919V12.0158C14.577 12.2195 14.4075 12.3845 14.2116 12.3845C14.0098 12.3845 13.8463 12.2283 13.8463 12.0158V11.2919C13.8463 11.0882 14.0157 10.9231 14.2116 10.9231ZM26.6345 10.9231C26.8364 10.9231 27 11.0795 27 11.2919V12.0158C27 12.2195 26.8304 12.3845 26.6345 12.3845C26.4328 12.3845 26.2692 12.2283 26.2692 12.0158V11.2919C26.2692 11.0882 26.4386 10.9231 26.6345 10.9231ZM26.6345 13.8462C26.8364 13.8462 27 14.0025 27 14.2149V14.9388C27 15.1425 26.8304 15.3077 26.6345 15.3077C26.4328 15.3077 26.2692 15.1512 26.2692 14.9388V14.2149C26.2692 14.0112 26.4386 13.8462 26.6345 13.8462ZM26.6345 16.7692C26.8364 16.7692 27 16.9256 27 17.138V17.862C27 18.0657 26.8304 18.2308 26.6345 18.2308C26.4328 18.2308 26.2692 18.0744 26.2692 17.862V17.138C26.2692 16.9343 26.4386 16.7692 26.6345 16.7692ZM20.7885 22.6156C20.9904 22.6156 21.1539 22.7719 21.1539 22.9843V23.7082C21.1539 23.9119 20.9844 24.0771 20.7885 24.0771C20.5867 24.0771 20.4232 23.9207 20.4232 23.7082V22.9843C20.4232 22.7806 20.5926 22.6156 20.7885 22.6156ZM17.862 8.73082H17.1381C16.9344 8.73082 16.7693 8.56138 16.7693 8.36549C16.7693 8.16363 16.9257 8 17.1381 8H17.862C18.0657 8 18.2309 8.16959 18.2309 8.36549C18.2309 8.56719 18.0745 8.73082 17.862 8.73082ZM20.7852 8.73082H20.0612C19.8575 8.73082 19.6924 8.56138 19.6924 8.36549C19.6924 8.16363 19.8488 8 20.0612 8H20.7852C20.9888 8 21.1539 8.16959 21.1539 8.36549C21.1539 8.56719 20.9976 8.73082 20.7852 8.73082ZM23.7081 8.73082H22.9842C22.7805 8.73082 22.6155 8.56138 22.6155 8.36549C22.6155 8.16363 22.7718 8 22.9842 8H23.7081C23.9118 8 24.077 8.16959 24.077 8.36549C24.077 8.56719 23.9205 8.73082 23.7081 8.73082ZM14.2116 9.46164C14.4134 9.46164 14.5769 9.29205 14.5769 9.09616V8.73082H14.9424C15.1441 8.73082 15.3077 8.56139 15.3077 8.36549C15.3077 8.16363 15.1513 8 14.9388 8H14.2149C14.0112 8 13.8462 8.15644 13.8462 8.36886V9.09279C13.8462 9.29648 14.0157 9.46164 14.2116 9.46164ZM26.6344 9.46164C26.4327 9.46164 26.2691 9.29205 26.2691 9.09616V8.73082H25.9038C25.7019 8.73082 25.5383 8.56139 25.5383 8.36549C25.5383 8.16363 25.6947 8 25.9071 8H26.6311C26.8348 8 26.9999 8.15644 26.9999 8.36886V9.09279C26.9999 9.29648 26.8303 9.46164 26.6344 9.46164ZM26.6344 19.6923C26.4327 19.6923 26.2691 19.8618 26.2691 20.0576V20.4231H25.9038C25.7019 20.4231 25.5383 20.5926 25.5383 20.7885C25.5383 20.9903 25.6947 21.1538 25.9071 21.1538H26.6311C26.8348 21.1538 26.9999 20.9975 26.9999 20.7851V20.0612C26.9999 19.8575 26.8303 19.6923 26.6344 19.6923ZM20.7884 25.5384C20.5866 25.5384 20.4231 25.7079 20.4231 25.9038V26.2692H20.0576C19.8559 26.2692 19.6923 26.4386 19.6923 26.6345C19.6923 26.8364 19.8487 27 20.0612 27H20.7851C20.9888 27 21.1538 26.8435 21.1538 26.6311V25.9072C21.1538 25.7035 20.9843 25.5384 20.7884 25.5384ZM8.36556 25.5384C8.56726 25.5384 8.73089 25.7079 8.73089 25.9038V26.2692H9.09622C9.29808 26.2692 9.46171 26.4386 9.46171 26.6345C9.46171 26.8364 9.30527 27 9.09286 27H8.36893C8.16524 27 8.00008 26.8435 8.00008 26.6311V25.9072C8.00008 25.7035 8.16967 25.5384 8.36556 25.5384ZM8.36556 16.7691C8.56726 16.7691 8.73089 16.9255 8.73089 17.1379V17.8619C8.73089 18.0656 8.56145 18.2307 8.36556 18.2307C8.16371 18.2307 8.00008 18.0743 8.00008 17.8619V17.1379C8.00008 16.9343 8.16967 16.7691 8.36556 16.7691ZM8.36556 19.6922C8.56726 19.6922 8.73089 19.8487 8.73089 20.0611V20.785C8.73089 20.9887 8.56145 21.1537 8.36556 21.1537C8.16371 21.1537 8.00008 20.9974 8.00008 20.785V20.0611C8.00008 19.8574 8.16967 19.6922 8.36556 19.6922ZM8.36556 22.6154C8.56726 22.6154 8.73089 22.7717 8.73089 22.9841V23.708C8.73089 23.9117 8.56145 24.0769 8.36556 24.0769C8.16371 24.0769 8.00008 23.9204 8.00008 23.708V22.9841C8.00008 22.7804 8.16967 22.6154 8.36556 22.6154ZM12.016 14.5766H11.292C11.0884 14.5766 10.9232 14.4072 10.9232 14.2113C10.9232 14.0094 11.0796 13.846 11.292 13.846H12.016C12.2197 13.846 12.3847 14.0154 12.3847 14.2113C12.3847 14.4131 12.2284 14.5766 12.016 14.5766ZM12.016 27H11.292C11.0884 27 10.9232 26.8304 10.9232 26.6345C10.9232 26.4328 11.0796 26.2692 11.292 26.2692H12.016C12.2197 26.2692 12.3847 26.4386 12.3847 26.6345C12.3847 26.8364 12.2284 27 12.016 27ZM14.9389 27H14.215C14.0113 27 13.8463 26.8304 13.8463 26.6345C13.8463 26.4328 14.0026 26.2692 14.215 26.2692H14.9389C15.1426 26.2692 15.3078 26.4386 15.3078 26.6345C15.3078 26.8364 15.1513 27 14.9389 27ZM17.862 27H17.1381C16.9344 27 16.7693 26.8304 16.7693 26.6345C16.7693 26.4328 16.9257 26.2692 17.1381 26.2692H17.862C18.0657 26.2692 18.2309 26.4386 18.2309 26.6345C18.2309 26.8364 18.0745 27 17.862 27ZM23.708 21.1536H22.9841C22.7804 21.1536 22.6154 20.9841 22.6154 20.7882C22.6154 20.5864 22.7717 20.4229 22.9841 20.4229H23.708C23.9117 20.4229 24.0769 20.5923 24.0769 20.7882C24.0769 20.9901 23.9204 21.1536 23.708 21.1536ZM8.36548 15.3075C8.56719 15.3075 8.73081 15.1381 8.73081 14.9422V14.5767H9.09614C9.298 14.5767 9.46163 14.4073 9.46163 14.2114C9.46163 14.0095 9.30519 13.846 9.09278 13.846H8.36885C8.16516 13.846 8 14.0023 8 14.2147V14.9387C8 15.1424 8.16959 15.3075 8.36548 15.3075Z"
      )
      .attr("fill-rule", "evenodd")
      .attr("clip-rule", "evenodd");
    intersect
      .append("rect")
      .attr("x", 14)
      .attr("y", 14)
      .attr("width", 7)
      .attr("height", 7);
    intersect
      .append("rect")
      .attr("x", 15)
      .attr("y", 9)
      .attr("width", 11)
      .attr("height", 11);

    let minus = defs
      .append("g")
      .attr("id", "minus")
      .attr("viewBox", "0 0 35 35")
      .attr("width", 35)
      .attr("height", 35);
    minus
      .append("rect")
      .attr("x", 1)
      .attr("y", 1)
      .attr("width", 33)
      .attr("height", 33)
      .attr("rx", 16.5)
      .attr("fill", "white");
    minus
      .append("path")
      .attr(
        "d",
        "M14.2187 13.8462H20.7815C20.9872 13.8462 21.1538 14.0126 21.1538 14.2186V20.7814C21.1538 20.9871 20.9875 21.1538 20.7815 21.1538H14.2187C14.013 21.1538 13.8463 20.9874 13.8463 20.7814V14.2186C13.8463 14.0129 14.0127 13.8462 14.2187 13.8462ZM20.4233 14.5769V20.4233H14.5769V14.5769H20.4233ZM14.2116 10.9231C14.4135 10.9231 14.577 11.0795 14.577 11.2919V12.0158C14.577 12.2195 14.4075 12.3845 14.2116 12.3845C14.0098 12.3845 13.8463 12.2283 13.8463 12.0158V11.2919C13.8463 11.0882 14.0157 10.9231 14.2116 10.9231ZM26.6345 10.9231C26.8364 10.9231 27 11.0795 27 11.2919V12.0158C27 12.2195 26.8304 12.3845 26.6345 12.3845C26.4328 12.3845 26.2692 12.2283 26.2692 12.0158V11.2919C26.2692 11.0882 26.4386 10.9231 26.6345 10.9231ZM26.6345 13.8462C26.8364 13.8462 27 14.0025 27 14.2149V14.9388C27 15.1425 26.8304 15.3077 26.6345 15.3077C26.4328 15.3077 26.2692 15.1512 26.2692 14.9388V14.2149C26.2692 14.0112 26.4386 13.8462 26.6345 13.8462ZM26.6345 16.7692C26.8364 16.7692 27 16.9256 27 17.138V17.862C27 18.0657 26.8304 18.2308 26.6345 18.2308C26.4328 18.2308 26.2692 18.0744 26.2692 17.862V17.138C26.2692 16.9343 26.4386 16.7692 26.6345 16.7692ZM20.7885 22.6156C20.9904 22.6156 21.1539 22.7719 21.1539 22.9843V23.7082C21.1539 23.9119 20.9844 24.0771 20.7885 24.0771C20.5867 24.0771 20.4232 23.9207 20.4232 23.7082V22.9843C20.4232 22.7806 20.5926 22.6156 20.7885 22.6156ZM17.862 8.73082H17.1381C16.9344 8.73082 16.7693 8.56138 16.7693 8.36549C16.7693 8.16363 16.9257 8 17.1381 8H17.862C18.0657 8 18.2309 8.16959 18.2309 8.36549C18.2309 8.56719 18.0745 8.73082 17.862 8.73082ZM20.7852 8.73082H20.0612C19.8575 8.73082 19.6924 8.56138 19.6924 8.36549C19.6924 8.16363 19.8488 8 20.0612 8H20.7852C20.9888 8 21.1539 8.16959 21.1539 8.36549C21.1539 8.56719 20.9976 8.73082 20.7852 8.73082ZM23.7081 8.73082H22.9842C22.7805 8.73082 22.6155 8.56138 22.6155 8.36549C22.6155 8.16363 22.7718 8 22.9842 8H23.7081C23.9118 8 24.077 8.16959 24.077 8.36549C24.077 8.56719 23.9205 8.73082 23.7081 8.73082ZM14.2116 9.46164C14.4134 9.46164 14.5769 9.29205 14.5769 9.09616V8.73082H14.9424C15.1441 8.73082 15.3077 8.56139 15.3077 8.36549C15.3077 8.16363 15.1513 8 14.9388 8H14.2149C14.0112 8 13.8462 8.15644 13.8462 8.36886V9.09279C13.8462 9.29648 14.0157 9.46164 14.2116 9.46164ZM26.6344 9.46164C26.4327 9.46164 26.2691 9.29205 26.2691 9.09616V8.73082H25.9038C25.7019 8.73082 25.5383 8.56139 25.5383 8.36549C25.5383 8.16363 25.6947 8 25.9071 8H26.6311C26.8348 8 26.9999 8.15644 26.9999 8.36886V9.09279C26.9999 9.29648 26.8303 9.46164 26.6344 9.46164ZM26.6344 19.6923C26.4327 19.6923 26.2691 19.8618 26.2691 20.0576V20.4231H25.9038C25.7019 20.4231 25.5383 20.5926 25.5383 20.7885C25.5383 20.9903 25.6947 21.1538 25.9071 21.1538H26.6311C26.8348 21.1538 26.9999 20.9975 26.9999 20.7851V20.0612C26.9999 19.8575 26.8303 19.6923 26.6344 19.6923ZM20.7884 25.5384C20.5866 25.5384 20.4231 25.7079 20.4231 25.9038V26.2692H20.0576C19.8559 26.2692 19.6923 26.4386 19.6923 26.6345C19.6923 26.8364 19.8487 27 20.0612 27H20.7851C20.9888 27 21.1538 26.8435 21.1538 26.6311V25.9072C21.1538 25.7035 20.9843 25.5384 20.7884 25.5384ZM8.36556 25.5384C8.56726 25.5384 8.73089 25.7079 8.73089 25.9038V26.2692H9.09622C9.29808 26.2692 9.46171 26.4386 9.46171 26.6345C9.46171 26.8364 9.30527 27 9.09286 27H8.36893C8.16524 27 8.00008 26.8435 8.00008 26.6311V25.9072C8.00008 25.7035 8.16967 25.5384 8.36556 25.5384ZM8.36556 16.7691C8.56726 16.7691 8.73089 16.9255 8.73089 17.1379V17.8619C8.73089 18.0656 8.56145 18.2307 8.36556 18.2307C8.16371 18.2307 8.00008 18.0743 8.00008 17.8619V17.1379C8.00008 16.9343 8.16967 16.7691 8.36556 16.7691ZM8.36556 19.6922C8.56726 19.6922 8.73089 19.8487 8.73089 20.0611V20.785C8.73089 20.9887 8.56145 21.1537 8.36556 21.1537C8.16371 21.1537 8.00008 20.9974 8.00008 20.785V20.0611C8.00008 19.8574 8.16967 19.6922 8.36556 19.6922ZM8.36556 22.6154C8.56726 22.6154 8.73089 22.7717 8.73089 22.9841V23.708C8.73089 23.9117 8.56145 24.0769 8.36556 24.0769C8.16371 24.0769 8.00008 23.9204 8.00008 23.708V22.9841C8.00008 22.7804 8.16967 22.6154 8.36556 22.6154ZM12.016 14.5766H11.292C11.0884 14.5766 10.9232 14.4072 10.9232 14.2113C10.9232 14.0094 11.0796 13.846 11.292 13.846H12.016C12.2197 13.846 12.3847 14.0154 12.3847 14.2113C12.3847 14.4131 12.2284 14.5766 12.016 14.5766ZM12.016 27H11.292C11.0884 27 10.9232 26.8304 10.9232 26.6345C10.9232 26.4328 11.0796 26.2692 11.292 26.2692H12.016C12.2197 26.2692 12.3847 26.4386 12.3847 26.6345C12.3847 26.8364 12.2284 27 12.016 27ZM14.9389 27H14.215C14.0113 27 13.8463 26.8304 13.8463 26.6345C13.8463 26.4328 14.0026 26.2692 14.215 26.2692H14.9389C15.1426 26.2692 15.3078 26.4386 15.3078 26.6345C15.3078 26.8364 15.1513 27 14.9389 27ZM17.862 27H17.1381C16.9344 27 16.7693 26.8304 16.7693 26.6345C16.7693 26.4328 16.9257 26.2692 17.1381 26.2692H17.862C18.0657 26.2692 18.2309 26.4386 18.2309 26.6345C18.2309 26.8364 18.0745 27 17.862 27ZM23.708 21.1536H22.9841C22.7804 21.1536 22.6154 20.9841 22.6154 20.7882C22.6154 20.5864 22.7717 20.4229 22.9841 20.4229H23.708C23.9117 20.4229 24.0769 20.5923 24.0769 20.7882C24.0769 20.9901 23.9204 21.1536 23.708 21.1536ZM8.36548 15.3075C8.56719 15.3075 8.73081 15.1381 8.73081 14.9422V14.5767H9.09614C9.298 14.5767 9.46163 14.4073 9.46163 14.2114C9.46163 14.0095 9.30519 13.846 9.09278 13.846H8.36885C8.16516 13.846 8 14.0023 8 14.2147V14.9387C8 15.1424 8.16959 15.3075 8.36548 15.3075Z"
      )
      .attr("fill-rule", "evenodd")
      .attr("clip-rule", "evenodd");
    minus
      .append("rect")
      .attr("x", 9)
      .attr("y", 15)
      .attr("width", 5)
      .attr("height", 11);
    minus
      .append("rect")
      .attr("x", 11)
      .attr("y", 21)
      .attr("width", 9)
      .attr("height", 5);

    let except = defs
      .append("g")
      .attr("id", "except")
      .attr("viewBox", "0 0 35 35")
      .attr("width", 35)
      .attr("height", 35);
    except
      .append("rect")
      .attr("x", 1)
      .attr("y", 1)
      .attr("width", 33)
      .attr("height", 33)
      .attr("rx", 16.5)
      .attr("fill", "white");
    except
      .append("path")
      .attr(
        "d",
        "M14.2187 13.8462H20.7815C20.9872 13.8462 21.1538 14.0126 21.1538 14.2186V20.7814C21.1538 20.9871 20.9875 21.1538 20.7815 21.1538H14.2187C14.013 21.1538 13.8463 20.9874 13.8463 20.7814V14.2186C13.8463 14.0129 14.0127 13.8462 14.2187 13.8462ZM20.4233 14.5769V20.4233H14.5769V14.5769H20.4233ZM14.2116 10.9231C14.4135 10.9231 14.577 11.0795 14.577 11.2919V12.0158C14.577 12.2195 14.4075 12.3845 14.2116 12.3845C14.0098 12.3845 13.8463 12.2283 13.8463 12.0158V11.2919C13.8463 11.0882 14.0157 10.9231 14.2116 10.9231ZM26.6345 10.9231C26.8364 10.9231 27 11.0795 27 11.2919V12.0158C27 12.2195 26.8304 12.3845 26.6345 12.3845C26.4328 12.3845 26.2692 12.2283 26.2692 12.0158V11.2919C26.2692 11.0882 26.4386 10.9231 26.6345 10.9231ZM26.6345 13.8462C26.8364 13.8462 27 14.0025 27 14.2149V14.9388C27 15.1425 26.8304 15.3077 26.6345 15.3077C26.4328 15.3077 26.2692 15.1512 26.2692 14.9388V14.2149C26.2692 14.0112 26.4386 13.8462 26.6345 13.8462ZM26.6345 16.7692C26.8364 16.7692 27 16.9256 27 17.138V17.862C27 18.0657 26.8304 18.2308 26.6345 18.2308C26.4328 18.2308 26.2692 18.0744 26.2692 17.862V17.138C26.2692 16.9343 26.4386 16.7692 26.6345 16.7692ZM20.7885 22.6156C20.9904 22.6156 21.1539 22.7719 21.1539 22.9843V23.7082C21.1539 23.9119 20.9844 24.0771 20.7885 24.0771C20.5867 24.0771 20.4232 23.9207 20.4232 23.7082V22.9843C20.4232 22.7806 20.5926 22.6156 20.7885 22.6156ZM17.862 8.73082H17.1381C16.9344 8.73082 16.7693 8.56138 16.7693 8.36549C16.7693 8.16363 16.9257 8 17.1381 8H17.862C18.0657 8 18.2309 8.16959 18.2309 8.36549C18.2309 8.56719 18.0745 8.73082 17.862 8.73082ZM20.7852 8.73082H20.0612C19.8575 8.73082 19.6924 8.56138 19.6924 8.36549C19.6924 8.16363 19.8488 8 20.0612 8H20.7852C20.9888 8 21.1539 8.16959 21.1539 8.36549C21.1539 8.56719 20.9976 8.73082 20.7852 8.73082ZM23.7081 8.73082H22.9842C22.7805 8.73082 22.6155 8.56138 22.6155 8.36549C22.6155 8.16363 22.7718 8 22.9842 8H23.7081C23.9118 8 24.077 8.16959 24.077 8.36549C24.077 8.56719 23.9205 8.73082 23.7081 8.73082ZM14.2116 9.46164C14.4134 9.46164 14.5769 9.29205 14.5769 9.09616V8.73082H14.9424C15.1441 8.73082 15.3077 8.56139 15.3077 8.36549C15.3077 8.16363 15.1513 8 14.9388 8H14.2149C14.0112 8 13.8462 8.15644 13.8462 8.36886V9.09279C13.8462 9.29648 14.0157 9.46164 14.2116 9.46164ZM26.6344 9.46164C26.4327 9.46164 26.2691 9.29205 26.2691 9.09616V8.73082H25.9038C25.7019 8.73082 25.5383 8.56139 25.5383 8.36549C25.5383 8.16363 25.6947 8 25.9071 8H26.6311C26.8348 8 26.9999 8.15644 26.9999 8.36886V9.09279C26.9999 9.29648 26.8303 9.46164 26.6344 9.46164ZM26.6344 19.6923C26.4327 19.6923 26.2691 19.8618 26.2691 20.0576V20.4231H25.9038C25.7019 20.4231 25.5383 20.5926 25.5383 20.7885C25.5383 20.9903 25.6947 21.1538 25.9071 21.1538H26.6311C26.8348 21.1538 26.9999 20.9975 26.9999 20.7851V20.0612C26.9999 19.8575 26.8303 19.6923 26.6344 19.6923ZM20.7884 25.5384C20.5866 25.5384 20.4231 25.7079 20.4231 25.9038V26.2692H20.0576C19.8559 26.2692 19.6923 26.4386 19.6923 26.6345C19.6923 26.8364 19.8487 27 20.0612 27H20.7851C20.9888 27 21.1538 26.8435 21.1538 26.6311V25.9072C21.1538 25.7035 20.9843 25.5384 20.7884 25.5384ZM8.36556 25.5384C8.56726 25.5384 8.73089 25.7079 8.73089 25.9038V26.2692H9.09622C9.29808 26.2692 9.46171 26.4386 9.46171 26.6345C9.46171 26.8364 9.30527 27 9.09286 27H8.36893C8.16524 27 8.00008 26.8435 8.00008 26.6311V25.9072C8.00008 25.7035 8.16967 25.5384 8.36556 25.5384ZM8.36556 16.7691C8.56726 16.7691 8.73089 16.9255 8.73089 17.1379V17.8619C8.73089 18.0656 8.56145 18.2307 8.36556 18.2307C8.16371 18.2307 8.00008 18.0743 8.00008 17.8619V17.1379C8.00008 16.9343 8.16967 16.7691 8.36556 16.7691ZM8.36556 19.6922C8.56726 19.6922 8.73089 19.8487 8.73089 20.0611V20.785C8.73089 20.9887 8.56145 21.1537 8.36556 21.1537C8.16371 21.1537 8.00008 20.9974 8.00008 20.785V20.0611C8.00008 19.8574 8.16967 19.6922 8.36556 19.6922ZM8.36556 22.6154C8.56726 22.6154 8.73089 22.7717 8.73089 22.9841V23.708C8.73089 23.9117 8.56145 24.0769 8.36556 24.0769C8.16371 24.0769 8.00008 23.9204 8.00008 23.708V22.9841C8.00008 22.7804 8.16967 22.6154 8.36556 22.6154ZM12.016 14.5766H11.292C11.0884 14.5766 10.9232 14.4072 10.9232 14.2113C10.9232 14.0094 11.0796 13.846 11.292 13.846H12.016C12.2197 13.846 12.3847 14.0154 12.3847 14.2113C12.3847 14.4131 12.2284 14.5766 12.016 14.5766ZM12.016 27H11.292C11.0884 27 10.9232 26.8304 10.9232 26.6345C10.9232 26.4328 11.0796 26.2692 11.292 26.2692H12.016C12.2197 26.2692 12.3847 26.4386 12.3847 26.6345C12.3847 26.8364 12.2284 27 12.016 27ZM14.9389 27H14.215C14.0113 27 13.8463 26.8304 13.8463 26.6345C13.8463 26.4328 14.0026 26.2692 14.215 26.2692H14.9389C15.1426 26.2692 15.3078 26.4386 15.3078 26.6345C15.3078 26.8364 15.1513 27 14.9389 27ZM17.862 27H17.1381C16.9344 27 16.7693 26.8304 16.7693 26.6345C16.7693 26.4328 16.9257 26.2692 17.1381 26.2692H17.862C18.0657 26.2692 18.2309 26.4386 18.2309 26.6345C18.2309 26.8364 18.0745 27 17.862 27ZM23.708 21.1536H22.9841C22.7804 21.1536 22.6154 20.9841 22.6154 20.7882C22.6154 20.5864 22.7717 20.4229 22.9841 20.4229H23.708C23.9117 20.4229 24.0769 20.5923 24.0769 20.7882C24.0769 20.9901 23.9204 21.1536 23.708 21.1536ZM8.36548 15.3075C8.56719 15.3075 8.73081 15.1381 8.73081 14.9422V14.5767H9.09614C9.298 14.5767 9.46163 14.4073 9.46163 14.2114C9.46163 14.0095 9.30519 13.846 9.09278 13.846H8.36885C8.16516 13.846 8 14.0023 8 14.2147V14.9387C8 15.1424 8.16959 15.3075 8.36548 15.3075Z"
      )
      .attr("fill-rule", "evenodd")
      .attr("clip-rule", "evenodd");
    except
      .append("rect")
      .attr("x", 9)
      .attr("y", 15)
      .attr("width", 5)
      .attr("height", 11);
    except
      .append("rect")
      .attr("x", 11)
      .attr("y", 21)
      .attr("width", 9)
      .attr("height", 5);

    // pivot
    let pivot = defs
      .append("g")
      .attr("id", "pivot")
      .attr("viewBox", "0 0 35 35")
      .attr("width", 35)
      .attr("height", 35);
    pivot
      .append("rect")
      .attr("x", 1)
      .attr("y", 1)
      .attr("width", 33)
      .attr("height", 33)
      .attr("rx", 16.5)
      .attr("fill", "white");
    pivot
      .append("path")
      .attr(
        "d",
        "M17.704 26.1121C22.3044 26.0027 26 22.2038 26 17.534C26 15.192 25.065 12.9978 23.4378 11.3942L22.213 12.6575C23.5063 13.9321 24.2479 15.6725 24.2479 17.5341C24.2479 21.2287 21.3362 24.2361 17.704 24.3447V23.4622L15.137 25.2312L17.704 27V26.1121ZM17.3635 8.95446C12.7317 9.02808 9 12.8413 9 17.534C9 19.7774 9.85786 21.8869 11.3658 23.4739L12.6308 22.2514C11.4324 20.9905 10.7521 19.3174 10.7521 17.5341C10.7521 13.8166 13.7001 10.7949 17.3635 10.7217V11.5377L19.9304 9.76883L17.3635 8V8.95446ZM17.5001 18.6921C18.208 18.6921 18.7819 18.1125 18.7819 17.3979C18.7819 16.683 18.208 16.1036 17.5001 16.1036C16.7921 16.1036 16.2181 16.683 16.2181 17.3979C16.2181 18.1125 16.7921 18.6921 17.5001 18.6921Z"
      )
      .attr("fill-rule", "evenodd")
      .attr("clip-rule", "evenodd");

    let unpivot = defs
      .append("g")
      .attr("id", "unpivot")
      .attr("viewBox", "0 0 35 35")
      .attr("width", 35)
      .attr("height", 35);
    unpivot
      .append("rect")
      .attr("x", 1)
      .attr("y", 1)
      .attr("width", 33)
      .attr("height", 33)
      .attr("rx", 16.5)
      .attr("fill", "white");
    unpivot
      .append("path")
      .attr(
        "d",
        "M17.704 26.1121C22.3044 26.0027 26 22.2038 26 17.534C26 15.192 25.065 12.9978 23.4378 11.3942L22.213 12.6575C23.5063 13.9321 24.2479 15.6725 24.2479 17.5341C24.2479 21.2287 21.3362 24.2361 17.704 24.3447V23.4622L15.137 25.2312L17.704 27V26.1121ZM17.3635 8.95446C12.7317 9.02808 9 12.8413 9 17.534C9 19.7774 9.85786 21.8869 11.3658 23.4739L12.6308 22.2514C11.4324 20.9905 10.7521 19.3174 10.7521 17.5341C10.7521 13.8166 13.7001 10.7949 17.3635 10.7217V11.5377L19.9304 9.76883L17.3635 8V8.95446Z"
      )
      .attr("fill-rule", "evenodd")
      .attr("clip-rule", "evenodd");
    unpivot
      .append("rect")
      .attr("x", 14)
      .attr("y", 17)
      .attr("width", 7)
      .attr("height", 1);

    // filter
    let filter = defs
      .append("g")
      .attr("id", "filter")
      .attr("viewBox", "0 0 35 35")
      .attr("width", 35)
      .attr("height", 35);
    filter
      .append("rect")
      .attr("x", 1)
      .attr("y", 1)
      .attr("width", 33)
      .attr("height", 33)
      .attr("rx", 16.5)
      .attr("fill", "white");
    filter
      .append("path")
      .attr(
        "d",
        "M10.1278 12.1578L15.645 19.4232V24.736C15.645 24.895 15.7357 25.0312 15.8493 25.1219L18.6418 26.8702C18.71 26.9156 18.8009 26.9383 18.8916 26.9383C18.9597 26.9383 19.0506 26.9156 19.1187 26.8702C19.2549 26.7794 19.3458 26.6431 19.3458 26.4842L19.346 19.4232L24.8631 12.1578L24.8858 12.1352C25.022 11.9081 25.0448 11.613 24.9084 11.386C24.7722 11.1588 24.5225 11 24.2727 11H10.7182C10.4458 11 10.1961 11.1362 10.0825 11.386C9.96897 11.6357 9.96897 11.9081 10.1052 12.1352L10.1278 12.1578L10.1278 12.1578ZM23.8866 11.908L18.5284 19.0145C18.4603 19.0826 18.4377 19.1961 18.4377 19.2869V25.6668L16.5532 24.4861L16.553 19.2868C16.553 19.1961 16.5304 19.0825 16.4623 19.0144L11.0814 11.908L23.8866 11.908Z"
      );
    filter
      .append("rect")
      .attr("x", 18)
      .attr("y", 15)
      .attr("width", 10)
      .attr("height", 10)
      .attr("rx", 5)
      .attr("fill", "#E8E8E8");
    filter
      .append("path")
      .attr(
        "d",
        "M20.9745 22V17.6364H23.6805V18.2031H21.6329V19.5327H23.4866V20.0973H21.6329V22H20.9745ZM24.439 22V18.7273H25.0761V22H24.439ZM24.7607 18.2223C24.6499 18.2223 24.5548 18.1854 24.4752 18.1115C24.3971 18.0362 24.358 17.9467 24.358 17.843C24.358 17.7379 24.3971 17.6484 24.4752 17.5746C24.5548 17.4993 24.6499 17.4616 24.7607 17.4616C24.8715 17.4616 24.966 17.4993 25.0441 17.5746C25.1237 17.6484 25.1634 17.7379 25.1634 17.843C25.1634 17.9467 25.1237 18.0362 25.0441 18.1115C24.966 18.1854 24.8715 18.2223 24.7607 18.2223Z"
      )
      .attr("strokeWidth", 0.3);

    let having = defs
      .append("g")
      .attr("id", "having")
      .attr("viewBox", "0 0 35 35")
      .attr("width", 35)
      .attr("height", 35);
    having
      .append("rect")
      .attr("x", 1)
      .attr("y", 1)
      .attr("width", 33)
      .attr("height", 33)
      .attr("rx", 16.5)
      .attr("fill", "white");
    having
      .append("path")
      .attr(
        "d",
        "M10.1278 12.1578L15.645 19.4232V24.736C15.645 24.895 15.7357 25.0312 15.8493 25.1219L18.6418 26.8702C18.71 26.9156 18.8009 26.9383 18.8916 26.9383C18.9597 26.9383 19.0506 26.9156 19.1187 26.8702C19.2549 26.7794 19.3458 26.6431 19.3458 26.4842L19.346 19.4232L24.8631 12.1578L24.8858 12.1352C25.022 11.9081 25.0448 11.613 24.9084 11.386C24.7722 11.1588 24.5225 11 24.2727 11H10.7182C10.4458 11 10.1961 11.1362 10.0825 11.386C9.96897 11.6357 9.96897 11.9081 10.1052 12.1352L10.1278 12.1578L10.1278 12.1578ZM23.8866 11.908L18.5284 19.0145C18.4603 19.0826 18.4377 19.1961 18.4377 19.2869V25.6668L16.5532 24.4861L16.553 19.2868C16.553 19.1961 16.5304 19.0825 16.4623 19.0144L11.0814 11.908L23.8866 11.908Z"
      );
    having
      .append("rect")
      .attr("x", 18)
      .attr("y", 15)
      .attr("width", 10)
      .attr("height", 10)
      .attr("rx", 5)
      .attr("fill", "#E8E8E8");
    having
      .append("path")
      .attr(
        "d",
        "M21.1814 22.2856V17.922H21.8398V19.8183H24.0152V17.922H24.6757V22.2856H24.0152V20.3829H21.8398V22.2856H21.1814Z"
      )
      .attr("strokeWidth", 0.3);

    let qualify = defs
      .append("g")
      .attr("id", "qualify")
      .attr("viewBox", "0 0 35 35")
      .attr("width", 35)
      .attr("height", 35);
    qualify
      .append("rect")
      .attr("x", 1)
      .attr("y", 1)
      .attr("width", 33)
      .attr("height", 33)
      .attr("rx", 16.5)
      .attr("fill", "white");
    qualify
      .append("path")
      .attr(
        "d",
        "M10.1278 12.1578L15.645 19.4232V24.736C15.645 24.895 15.7357 25.0312 15.8493 25.1219L18.6418 26.8702C18.71 26.9156 18.8009 26.9383 18.8916 26.9383C18.9597 26.9383 19.0506 26.9156 19.1187 26.8702C19.2549 26.7794 19.3458 26.6431 19.3458 26.4842L19.346 19.4232L24.8631 12.1578L24.8858 12.1352C25.022 11.9081 25.0448 11.613 24.9084 11.386C24.7722 11.1588 24.5225 11 24.2727 11H10.7182C10.4458 11 10.1961 11.1362 10.0825 11.386C9.96897 11.6357 9.96897 11.9081 10.1052 12.1352L10.1278 12.1578L10.1278 12.1578ZM23.8866 11.908L18.5284 19.0145C18.4603 19.0826 18.4377 19.1961 18.4377 19.2869V25.6668L16.5532 24.4861L16.553 19.2868C16.553 19.1961 16.5304 19.0825 16.4623 19.0144L11.0814 11.908L23.8866 11.908Z"
      );
    qualify
      .append("rect")
      .attr("x", 18)
      .attr("y", 15)
      .attr("width", 10)
      .attr("height", 10)
      .attr("rx", 5)
      .attr("fill", "#E8E8E8");
    qualify
      .append("path")
      .attr(
        "d",
        "M22.7067 20.8709H23.3544L23.8104 21.4653L24.0256 21.7295L24.7522 22.6777H24.0789L23.5931 22.0449L23.4269 21.8148L22.7067 20.8709ZM24.8928 20.1038C24.8928 20.5697 24.8076 20.9703 24.6371 21.3055C24.4667 21.6393 24.233 21.8964 23.9361 22.0768C23.6407 22.2558 23.3047 22.3453 22.9283 22.3453C22.5505 22.3453 22.2131 22.2558 21.9162 22.0768C21.6208 21.8964 21.3878 21.6386 21.2174 21.3034C21.0469 20.9682 20.9617 20.5683 20.9617 20.1038C20.9617 19.6379 21.0469 19.2381 21.2174 18.9043C21.3878 18.569 21.6208 18.3119 21.9162 18.1329C22.2131 17.9525 22.5505 17.8623 22.9283 17.8623C23.3047 17.8623 23.6407 17.9525 23.9361 18.1329C24.233 18.3119 24.4667 18.569 24.6371 18.9043C24.8076 19.2381 24.8928 19.6379 24.8928 20.1038ZM24.2408 20.1038C24.2408 19.7487 24.1833 19.4497 24.0682 19.2068C23.9546 18.9625 23.7983 18.7778 23.5995 18.6528C23.402 18.5264 23.1783 18.4632 22.9283 18.4632C22.6769 18.4632 22.4525 18.5264 22.255 18.6528C22.0576 18.7778 21.9013 18.9625 21.7863 19.2068C21.6726 19.4497 21.6158 19.7487 21.6158 20.1038C21.6158 20.4589 21.6726 20.7587 21.7863 21.003C21.9013 21.2459 22.0576 21.4305 22.255 21.557C22.4525 21.682 22.6769 21.7445 22.9283 21.7445C23.1783 21.7445 23.402 21.682 23.5995 21.557C23.7983 21.4305 23.9546 21.2459 24.0682 21.003C24.1833 20.7587 24.2408 20.4589 24.2408 20.1038Z"
      )
      .attr("strokeWidth", 0.3);

    let sample = defs
      .append("g")
      .attr("id", "sample")
      .attr("viewBox", "0 0 35 35")
      .attr("width", 35)
      .attr("height", 35);
    sample
      .append("rect")
      .attr("x", 1)
      .attr("y", 1)
      .attr("width", 33)
      .attr("height", 33)
      .attr("rx", 16.5)
      .attr("fill", "white");
    sample
      .append("path")
      .attr(
        "d",
        "M10.1278 12.1578L15.645 19.4232V24.736C15.645 24.895 15.7357 25.0312 15.8493 25.1219L18.6418 26.8702C18.71 26.9156 18.8009 26.9383 18.8916 26.9383C18.9597 26.9383 19.0506 26.9156 19.1187 26.8702C19.2549 26.7794 19.3458 26.6431 19.3458 26.4842L19.346 19.4232L24.8631 12.1578L24.8858 12.1352C25.022 11.9081 25.0448 11.613 24.9084 11.386C24.7722 11.1588 24.5225 11 24.2727 11H10.7182C10.4458 11 10.1961 11.1362 10.0825 11.386C9.96897 11.6357 9.96897 11.9081 10.1052 12.1352L10.1278 12.1578L10.1278 12.1578ZM23.8866 11.908L18.5284 19.0145C18.4603 19.0826 18.4377 19.1961 18.4377 19.2869V25.6668L16.5532 24.4861L16.553 19.2868C16.553 19.1961 16.5304 19.0825 16.4623 19.0144L11.0814 11.908L23.8866 11.908Z"
      );
    sample
      .append("rect")
      .attr("x", 18)
      .attr("y", 15)
      .attr("width", 10)
      .attr("height", 10)
      .attr("rx", 5)
      .attr("fill", "#E8E8E8");
    sample
      .append("path")
      .attr(
        "d",
        "M23.8573 19.0683C23.8346 18.8666 23.7408 18.7104 23.576 18.5996C23.4113 18.4873 23.2039 18.4312 22.9539 18.4312C22.7749 18.4312 22.6201 18.4597 22.4894 18.5165C22.3587 18.5719 22.2571 18.6486 22.1847 18.7466C22.1137 18.8432 22.0782 18.9533 22.0782 19.0768C22.0782 19.1805 22.1023 19.27 22.1506 19.3453C22.2003 19.4206 22.265 19.4838 22.3445 19.5349C22.4255 19.5847 22.5121 19.6266 22.6044 19.6606C22.6968 19.6933 22.7855 19.7203 22.8708 19.7416L23.2969 19.8524C23.4361 19.8865 23.5789 19.9327 23.7252 19.9909C23.8715 20.0491 24.0071 20.1258 24.1321 20.221C24.2571 20.3162 24.358 20.4341 24.4347 20.5747C24.5128 20.7153 24.5519 20.8837 24.5519 21.0797C24.5519 21.3268 24.488 21.5463 24.3601 21.7381C24.2337 21.9298 24.0498 22.0811 23.8083 22.1919C23.5682 22.3027 23.2777 22.3581 22.9368 22.3581C22.6101 22.3581 22.3275 22.3062 22.0888 22.2025C21.8502 22.0989 21.6634 21.9518 21.5284 21.7615C21.3935 21.5697 21.3189 21.3425 21.3047 21.0797H21.9652C21.978 21.2373 22.0292 21.3687 22.1186 21.4739C22.2096 21.5775 22.3253 21.655 22.4659 21.7061C22.608 21.7558 22.7635 21.7807 22.9326 21.7807C23.1186 21.7807 23.2841 21.7516 23.429 21.6933C23.5753 21.6337 23.6904 21.5513 23.7742 21.4462C23.858 21.3396 23.8999 21.2153 23.8999 21.0733C23.8999 20.944 23.863 20.8382 23.7891 20.7558C23.7167 20.6734 23.6179 20.6052 23.4929 20.5513C23.3694 20.4973 23.2294 20.4497 23.0732 20.4085L22.5576 20.2679C22.2081 20.1727 21.9311 20.0328 21.7266 19.8481C21.5235 19.6635 21.4219 19.4192 21.4219 19.1152C21.4219 18.8638 21.4901 18.6443 21.6265 18.4568C21.7628 18.2693 21.9475 18.1237 22.1804 18.02C22.4134 17.9149 22.6762 17.8623 22.9688 17.8623C23.2642 17.8623 23.5249 17.9142 23.7507 18.0179C23.978 18.1216 24.157 18.2643 24.2877 18.4462C24.4184 18.6266 24.4865 18.8339 24.4922 19.0683H23.8573Z"
      )
      .attr("strokeWidth", 0.3);

    let limit = defs
      .append("g")
      .attr("id", "limit")
      .attr("viewBox", "0 0 35 35")
      .attr("width", 35)
      .attr("height", 35);
    limit
      .append("rect")
      .attr("x", 1)
      .attr("y", 1)
      .attr("width", 33)
      .attr("height", 33)
      .attr("rx", 16.5)
      .attr("fill", "white");
    limit
      .append("path")
      .attr(
        "d",
        "M10.1278 12.1578L15.645 19.4232V24.736C15.645 24.895 15.7357 25.0312 15.8493 25.1219L18.6418 26.8702C18.71 26.9156 18.8009 26.9383 18.8916 26.9383C18.9597 26.9383 19.0506 26.9156 19.1187 26.8702C19.2549 26.7794 19.3458 26.6431 19.3458 26.4842L19.346 19.4232L24.8631 12.1578L24.8858 12.1352C25.022 11.9081 25.0448 11.613 24.9084 11.386C24.7722 11.1588 24.5225 11 24.2727 11H10.7182C10.4458 11 10.1961 11.1362 10.0825 11.386C9.96897 11.6357 9.96897 11.9081 10.1052 12.1352L10.1278 12.1578L10.1278 12.1578ZM23.8866 11.908L18.5284 19.0145C18.4603 19.0826 18.4377 19.1961 18.4377 19.2869V25.6668L16.5532 24.4861L16.553 19.2868C16.553 19.1961 16.5304 19.0825 16.4623 19.0144L11.0814 11.908L23.8866 11.908Z"
      );
    limit
      .append("rect")
      .attr("x", 18)
      .attr("y", 15)
      .attr("width", 10)
      .attr("height", 10)
      .attr("rx", 5)
      .attr("fill", "#E8E8E8");
    limit
      .append("path")
      .attr(
        "d",
        "M21.7146 22.2856V17.922H22.373V21.7189H24.3503V22.2856H21.7146Z"
      )
      .attr("strokeWidth", 0.3);

    let top = defs
      .append("g")
      .attr("id", "top")
      .attr("viewBox", "0 0 35 35")
      .attr("width", 35)
      .attr("height", 35);
    top
      .append("rect")
      .attr("x", 1)
      .attr("y", 1)
      .attr("width", 33)
      .attr("height", 33)
      .attr("rx", 16.5)
      .attr("fill", "white");
    top
      .append("path")
      .attr(
        "d",
        "M10.1278 12.1578L15.645 19.4232V24.736C15.645 24.895 15.7357 25.0312 15.8493 25.1219L18.6418 26.8702C18.71 26.9156 18.8009 26.9383 18.8916 26.9383C18.9597 26.9383 19.0506 26.9156 19.1187 26.8702C19.2549 26.7794 19.3458 26.6431 19.3458 26.4842L19.346 19.4232L24.8631 12.1578L24.8858 12.1352C25.022 11.9081 25.0448 11.613 24.9084 11.386C24.7722 11.1588 24.5225 11 24.2727 11H10.7182C10.4458 11 10.1961 11.1362 10.0825 11.386C9.96897 11.6357 9.96897 11.9081 10.1052 12.1352L10.1278 12.1578L10.1278 12.1578ZM23.8866 11.908L18.5284 19.0145C18.4603 19.0826 18.4377 19.1961 18.4377 19.2869V25.6668L16.5532 24.4861L16.553 19.2868C16.553 19.1961 16.5304 19.0825 16.4623 19.0144L11.0814 11.908L23.8866 11.908Z"
      );
    top
      .append("rect")
      .attr("x", 18)
      .attr("y", 15)
      .attr("width", 10)
      .attr("height", 10)
      .attr("rx", 5)
      .attr("fill", "#E8E8E8");
    top
      .append("path")
      .attr(
        "d",
        "M21.2387 18.4888V17.922H24.6158V18.4888H23.2543V22.2856H22.598V18.4888H21.2387Z"
      )
      .attr("strokeWidth", 0.3);

    let fetch = defs
      .append("g")
      .attr("id", "fetch")
      .attr("viewBox", "0 0 35 35")
      .attr("width", 35)
      .attr("height", 35);
    fetch
      .append("rect")
      .attr("x", 1)
      .attr("y", 1)
      .attr("width", 33)
      .attr("height", 33)
      .attr("rx", 16.5)
      .attr("fill", "white");
    fetch
      .append("path")
      .attr(
        "d",
        "M10.1278 12.1578L15.645 19.4232V24.736C15.645 24.895 15.7357 25.0312 15.8493 25.1219L18.6418 26.8702C18.71 26.9156 18.8009 26.9383 18.8916 26.9383C18.9597 26.9383 19.0506 26.9156 19.1187 26.8702C19.2549 26.7794 19.3458 26.6431 19.3458 26.4842L19.346 19.4232L24.8631 12.1578L24.8858 12.1352C25.022 11.9081 25.0448 11.613 24.9084 11.386C24.7722 11.1588 24.5225 11 24.2727 11H10.7182C10.4458 11 10.1961 11.1362 10.0825 11.386C9.96897 11.6357 9.96897 11.9081 10.1052 12.1352L10.1278 12.1578L10.1278 12.1578ZM23.8866 11.908L18.5284 19.0145C18.4603 19.0826 18.4377 19.1961 18.4377 19.2869V25.6668L16.5532 24.4861L16.553 19.2868C16.553 19.1961 16.5304 19.0825 16.4623 19.0144L11.0814 11.908L23.8866 11.908Z"
      );
    fetch
      .append("rect")
      .attr("x", 18)
      .attr("y", 15)
      .attr("width", 10)
      .attr("height", 10)
      .attr("rx", 5)
      .attr("fill", "#E8E8E8");
    fetch
      .append("path")
      .attr(
        "d",
        "M20.581 22V17.6364H23.2869V18.2031H21.2393V19.5327H23.093V20.0973H21.2393V22H20.581ZM25.2157 22.0661C24.8933 22.0661 24.6156 21.9972 24.3826 21.8594C24.1511 21.7202 23.9721 21.5249 23.8457 21.2734C23.7207 21.0206 23.6582 20.7244 23.6582 20.3849C23.6582 20.0497 23.7207 19.7543 23.8457 19.4986C23.9721 19.2429 24.1483 19.0433 24.3741 18.8999C24.6014 18.7564 24.867 18.6847 25.171 18.6847C25.3556 18.6847 25.5346 18.7152 25.7079 18.7763C25.8812 18.8374 26.0368 18.9332 26.1745 19.0639C26.3123 19.1946 26.421 19.3643 26.5005 19.5732C26.5801 19.7805 26.6199 20.0327 26.6199 20.3295V20.5554H24.0183V20.0781H25.9956C25.9956 19.9105 25.9615 19.7621 25.8933 19.6328C25.8251 19.5021 25.7292 19.3991 25.6056 19.3239C25.4835 19.2486 25.34 19.2109 25.1752 19.2109C24.9963 19.2109 24.84 19.255 24.7065 19.343C24.5744 19.4297 24.4721 19.5433 24.3997 19.6839C24.3287 19.8232 24.2931 19.9744 24.2931 20.1378V20.5107C24.2931 20.7294 24.3315 20.9155 24.4082 21.0689C24.4863 21.2223 24.595 21.3395 24.7342 21.4205C24.8734 21.5 25.036 21.5398 25.2221 21.5398C25.3429 21.5398 25.4529 21.5227 25.5524 21.4886C25.6518 21.4531 25.7377 21.4006 25.8102 21.331C25.8826 21.2614 25.938 21.1754 25.9764 21.0732L26.5794 21.1818C26.5311 21.3594 26.4444 21.5149 26.3194 21.6484C26.1958 21.7805 26.0403 21.8835 25.8528 21.9574C25.6667 22.0298 25.4544 22.0661 25.2157 22.0661Z"
      )
      .attr("strokeWidth", 0.3);

    // join
    let join = defs
      .append("g")
      .attr("id", "join")
      .attr("viewBox", "0 0 35 35")
      .attr("width", 35)
      .attr("height", 35);
    join
      .append("rect")
      .attr("x", 1)
      .attr("y", 1)
      .attr("width", 33)
      .attr("height", 33)
      .attr("rx", 16.5)
      .attr("fill", "white");
    join
      .append("path")
      .attr(
        "d",
        "M22.9999 19.596L22.1666 19.5854C22.1599 20.1154 22.0914 20.6364 21.9636 21.1412L22.7714 21.3457C22.9152 20.7776 22.9924 20.1915 22.9999 19.596L22.9999 19.596ZM22.2263 16.1773L21.4795 16.5469C21.7115 17.016 21.8877 17.5114 22.0035 18.0247L22.8165 17.8412C22.686 17.2633 22.4876 16.7054 22.2263 16.1773L22.2263 16.1773ZM19.9706 13.4765L19.4734 14.1453C19.8946 14.4584 20.2778 14.8202 20.6146 15.2225L21.2536 14.6876C20.8749 14.2351 20.4441 13.8284 19.9706 13.4765ZM16.6528 12.0872L16.5257 12.9109C17.0467 12.9912 17.553 13.1327 18.0362 13.3316L18.3535 12.5611C17.8094 12.337 17.2392 12.1777 16.6528 12.0872L16.6528 12.0872ZM13.5616 12.2522L13.7764 13.0574C14.1784 12.9501 14.5912 12.8806 15.011 12.8502L14.9507 12.019C14.4786 12.0532 14.014 12.1314 13.5615 12.2522L13.5616 12.2522Z"
      )
      .attr("fill-rule", "evenodd")
      .attr("clip-rule", "evenodd");
    join
      .append("path")
      .attr(
        "d",
        "M13.0053 14.2172L13.8382 14.248C13.8578 13.719 13.9392 13.1998 14.0796 12.698L13.2771 12.4734C13.119 13.0382 13.0274 13.6225 13.0053 14.2172L13.0053 14.2172ZM13.6941 17.6544L14.45 17.3035C14.2297 16.8291 14.0661 16.3295 13.9628 15.8132L13.1457 15.9766C13.2617 16.5577 13.4462 17.1203 13.6941 17.6544H13.6941ZM15.881 20.4087L16.3947 19.7524C15.9816 19.429 15.6074 19.058 15.2807 18.6475L14.6287 19.1663C14.9959 19.628 15.4166 20.0451 15.881 20.4087V20.4087ZM19.1578 21.8799L19.3061 21.0597C18.7878 20.9661 18.2853 20.8115 17.807 20.6L17.4699 21.3622C18.0083 21.6003 18.5743 21.7743 19.1578 21.8799H19.1578ZM22.4627 21.7404L22.2452 20.9358C21.7846 21.0604 21.3095 21.1353 20.8258 21.1586L20.8659 21.991C21.4096 21.9649 21.9442 21.8806 22.4628 21.7404L22.4627 21.7404Z"
      )
      .attr("fill-rule", "evenodd")
      .attr("clip-rule", "evenodd");
    join
      .append("path")
      .attr(
        "d",
        "M8.00015 19.4996C8.00015 23.6419 11.3582 26.9999 15.5005 26.9999C18.9535 26.9999 21.9361 24.6471 22.7719 21.3457L21.9641 21.1412C21.2213 24.0748 18.5698 26.1664 15.5006 26.1664C11.8186 26.1664 8.83351 23.1815 8.83351 19.4993C8.83351 16.4621 10.882 13.8312 13.7737 13.058L13.5585 12.2528C10.3043 13.1228 8 16.0823 8 19.4992L8.00015 19.4996Z"
      )
      .attr("fill-rule", "evenodd")
      .attr("clip-rule", "evenodd");
    join
      .append("path")
      .attr(
        "d",
        "M28.0012 14.4994C28.0012 10.357 24.6432 6.99902 20.5009 6.99902C17.1158 6.99902 14.1767 9.26084 13.2773 12.4732L14.0798 12.6978C14.879 9.84304 17.4918 7.83224 20.5008 7.83224C24.1828 7.83224 27.1679 10.8172 27.1679 14.4993C27.1679 17.5287 25.1296 20.1549 22.2481 20.9353L22.4658 21.7397C25.7086 20.8618 28.0014 17.9078 28.0014 14.4994L28.0012 14.4994Z"
      )
      .attr("fill-rule", "evenodd")
      .attr("clip-rule", "evenodd");

    let left_join = defs
      .append("g")
      .attr("id", "left_join")
      .attr("viewBox", "0 0 35 35")
      .attr("width", 35)
      .attr("height", 35);
    left_join
      .append("rect")
      .attr("x", 1)
      .attr("y", 1)
      .attr("width", 33)
      .attr("height", 33)
      .attr("rx", 16.5)
      .attr("fill", "white");
    left_join
      .append("path")
      .attr(
        "d",
        "M7.44354 15.6633C5.35554 15.6633 3.59294 14.9447 2.15575 13.5075C0.718584 12.0703 0 10.3077 0 8.21973C0 6.13172 0.718584 4.36408 2.15575 2.91681C3.59294 1.46955 5.35554 0.745926 7.44354 0.745926C8.26284 0.745926 9.04024 0.870449 9.77576 1.1195C10.5113 1.36854 11.1793 1.70643 11.7798 2.13315C10.8907 2.9059 10.2102 3.822 9.73851 4.88142C9.26679 5.94084 9.03092 7.05613 9.03092 8.22729C9.03092 9.39845 9.26679 10.5112 9.73851 11.5656C10.2102 12.62 10.8907 13.5235 11.7798 14.276C11.1793 14.7229 10.5113 15.0659 9.77576 15.3048C9.04024 15.5438 8.26284 15.6633 7.44354 15.6633ZM17.1262 15.6633C16.3027 15.6633 15.5308 15.5441 14.8104 15.3056C14.0901 15.0672 13.4267 14.724 12.8202 14.276C12.9789 14.1258 13.1423 13.968 13.3104 13.8026C13.4786 13.6372 13.6294 13.4552 13.7629 13.2566C14.2144 13.6197 14.7272 13.8908 15.3012 14.07C15.8751 14.2493 16.4835 14.3389 17.1262 14.3389C18.8205 14.3389 20.2689 13.7426 21.4716 12.55C22.6743 11.3574 23.2756 9.91399 23.2756 8.21973C23.2756 6.52546 22.6743 5.077 21.4716 3.87433C20.2689 2.67165 18.8205 2.0703 17.1262 2.0703C16.4835 2.0703 15.8751 2.16496 15.3012 2.35428C14.7272 2.54357 14.2144 2.81977 13.7629 3.18287C13.6294 2.98425 13.4786 2.80225 13.3104 2.63686C13.1423 2.47147 12.9789 2.30356 12.8202 2.13315C13.4267 1.70536 14.0901 1.36721 14.8104 1.11871C15.5308 0.870187 16.3027 0.745926 17.1262 0.745926C19.2142 0.745926 20.9818 1.46955 22.4291 2.91681C23.8764 4.36408 24.6 6.13172 24.6 8.21973C24.6 10.3077 23.8764 12.0703 22.4291 13.5075C20.9818 14.9447 19.2142 15.6633 17.1262 15.6633ZM12.2849 13.8082C11.4589 13.1265 10.8188 12.2985 10.3643 11.3243C9.90988 10.3501 9.68266 9.31524 9.68266 8.21973C9.68266 7.12421 9.90988 6.08936 10.3643 5.11516C10.8188 4.14095 11.4589 3.30288 12.2849 2.60097C13.1108 3.30288 13.756 4.14095 14.2206 5.11516C14.6851 6.08936 14.9173 7.12421 14.9173 8.21973C14.9173 9.31524 14.6851 10.3501 14.2206 11.3243C13.756 12.2985 13.1108 13.1265 12.2849 13.8082Z"
      )
      .attr("fill", "black")
      .attr("class", "transform");

    let right_join = defs
      .append("g")
      .attr("id", "right_join")
      .attr("viewBox", "0 0 35 35")
      .attr("width", 35)
      .attr("height", 35);
    right_join
      .append("rect")
      .attr("x", 1)
      .attr("y", 1)
      .attr("width", 33)
      .attr("height", 33)
      .attr("rx", 16.5)
      .attr("fill", "white");
    right_join
      .append("path")
      .attr(
        "d",
        "M17.1262 15.5806C16.3027 15.5806 15.5308 15.4614 14.8104 15.223C14.0901 14.9845 13.4267 14.6414 12.8202 14.1934C13.6892 13.4408 14.3645 12.5348 14.8464 11.4754C15.3282 10.416 15.5691 9.30066 15.5691 8.1295C15.5691 6.95835 15.3282 5.84558 14.8464 4.7912C14.3645 3.73682 13.6892 2.82325 12.8202 2.05049C13.4267 1.6227 14.0901 1.28455 14.8104 1.03605C15.5308 0.78753 16.3027 0.663269 17.1262 0.663269C19.2142 0.663269 20.9818 1.3869 22.4291 2.83415C23.8764 4.28143 24.6 6.04907 24.6 8.13707C24.6 10.2251 23.8764 11.9877 22.4291 13.4249C20.9818 14.862 19.2142 15.5806 17.1262 15.5806ZM7.44354 15.5806C5.35554 15.5806 3.59294 14.862 2.15575 13.4249C0.718584 11.9877 0 10.2251 0 8.13707C0 6.04907 0.718584 4.28143 2.15575 2.83415C3.59294 1.3869 5.35554 0.663269 7.44354 0.663269C8.26284 0.663269 9.04024 0.787792 9.77576 1.03684C10.5113 1.28588 11.1793 1.62377 11.7798 2.05049C11.5937 2.22091 11.4184 2.38881 11.2538 2.5542C11.0893 2.71959 10.9403 2.9016 10.8069 3.10021C10.3398 2.73711 9.82232 2.46091 9.25441 2.27162C8.68652 2.0823 8.0829 1.98764 7.44354 1.98764C5.74928 1.98764 4.30586 2.58899 3.11328 3.79168C1.92068 4.99434 1.32438 6.44281 1.32438 8.13707C1.32438 9.83133 1.92068 11.2748 3.11328 12.4673C4.30586 13.6599 5.74928 14.2562 7.44354 14.2562C8.0829 14.2562 8.68652 14.1666 9.25441 13.9874C9.82232 13.8082 10.3398 13.537 10.8069 13.1739C10.9403 13.3725 11.0893 13.5545 11.2538 13.7199C11.4184 13.8853 11.5937 14.0431 11.7798 14.1934C11.1793 14.6403 10.5113 14.9832 9.77576 15.2222C9.04024 15.4611 8.26284 15.5806 7.44354 15.5806ZM12.2849 13.7256C11.4589 13.0438 10.8188 12.2158 10.3643 11.2416C9.90988 10.2674 9.68266 9.23258 9.68266 8.13707C9.68266 7.04156 9.90988 6.0067 10.3643 5.03251C10.8188 4.05829 11.4589 3.22023 12.2849 2.51831C13.1108 3.22023 13.756 4.05829 14.2206 5.03251C14.6851 6.0067 14.9173 7.04156 14.9173 8.13707C14.9173 9.23258 14.6851 10.2674 14.2206 11.2416C13.756 12.2158 13.1108 13.0438 12.2849 13.7256Z"
      )
      .attr("fill", "black")
      .attr("class", "transform");

    let full_join = defs
      .append("g")
      .attr("id", "full_join")
      .attr("viewBox", "0 0 35 35")
      .attr("width", 35)
      .attr("height", 35);
    full_join
      .append("rect")
      .attr("x", 1)
      .attr("y", 1)
      .attr("width", 33)
      .attr("height", 33)
      .attr("rx", 16.5)
      .attr("fill", "white");
    full_join
      .append("path")
      .attr(
        "d",
        "M17.1262 15.7459C16.3812 15.7459 15.6837 15.6427 15.0336 15.4364C14.3835 15.23 13.7885 14.9282 13.2485 14.5309C14.0973 13.6372 14.7699 12.6579 15.2665 11.5932C15.763 10.5285 16.0113 9.42868 16.0113 8.29385C16.0113 7.15902 15.763 6.06209 15.2665 5.00305C14.7699 3.94401 14.0973 2.9676 13.2485 2.07383C13.7885 1.67658 14.3835 1.36972 15.0336 1.15325C15.6837 0.936807 16.3812 0.828583 17.1262 0.828583C19.2142 0.828583 20.9818 1.55221 22.4291 2.99946C23.8764 4.44674 24.6 6.21438 24.6 8.30238C24.6 10.3904 23.8764 12.153 22.4291 13.5902C20.9818 15.0273 19.2142 15.7459 17.1262 15.7459ZM12.2849 13.8909C11.4919 13.2329 10.86 12.4222 10.3891 11.4586C9.91813 10.495 9.68266 9.4429 9.68266 8.30238C9.68266 7.16187 9.91813 6.10981 10.3891 5.1462C10.86 4.18259 11.4919 3.36174 12.2849 2.68363C13.098 3.36174 13.74 4.18259 14.2109 5.1462C14.6819 6.10981 14.9173 7.16187 14.9173 8.30238C14.9173 9.4429 14.6819 10.495 14.2109 11.4586C13.74 12.4222 13.098 13.2329 12.2849 13.8909ZM7.44354 15.7459C5.35554 15.7459 3.59294 15.0273 2.15575 13.5902C0.718584 12.153 0 10.3904 0 8.30238C0 6.21438 0.718584 4.44674 2.15575 2.99946C3.59294 1.55221 5.35554 0.828583 7.44354 0.828583C8.18854 0.828583 8.88724 0.936807 9.53963 1.15325C10.192 1.36972 10.796 1.67658 11.3515 2.07383C10.4826 2.9676 9.80485 3.94554 9.31838 5.00765C8.83192 6.06973 8.5887 7.16677 8.5887 8.29875C8.5887 9.54255 8.83464 10.699 9.32652 11.7681C9.81842 12.8373 10.481 13.7605 11.3143 14.5379C10.7766 14.9298 10.1813 15.2292 9.5284 15.4359C8.87551 15.6426 8.18055 15.7459 7.44354 15.7459Z"
      )
      .attr("fill", "black")
      .attr("class", "transform");

    let cross_join = defs
      .append("g")
      .attr("id", "cross_join")
      .attr("viewBox", "0 0 35 35")
      .attr("width", 35)
      .attr("height", 35);
    cross_join
      .append("rect")
      .attr("x", 1)
      .attr("y", 1)
      .attr("width", 33)
      .attr("height", 33)
      .attr("rx", 16.5)
      .attr("fill", "white");
    cross_join
      .append("path")
      .attr(
        "d",
        "M22.9999 19.596L22.1666 19.5854C22.1599 20.1154 22.0914 20.6364 21.9636 21.1412L22.7714 21.3457C22.9152 20.7776 22.9924 20.1915 22.9999 19.596L22.9999 19.596ZM22.2263 16.1773L21.4795 16.5469C21.7115 17.016 21.8877 17.5114 22.0035 18.0247L22.8165 17.8412C22.686 17.2633 22.4876 16.7054 22.2263 16.1773L22.2263 16.1773ZM19.9706 13.4765L19.4734 14.1453C19.8946 14.4584 20.2778 14.8202 20.6146 15.2225L21.2536 14.6876C20.8749 14.2351 20.4441 13.8284 19.9706 13.4765ZM16.6528 12.0872L16.5257 12.9109C17.0467 12.9912 17.553 13.1327 18.0362 13.3316L18.3535 12.5611C17.8094 12.337 17.2392 12.1777 16.6528 12.0872L16.6528 12.0872ZM13.5616 12.2522L13.7764 13.0574C14.1784 12.9501 14.5912 12.8806 15.011 12.8502L14.9507 12.019C14.4786 12.0532 14.014 12.1314 13.5615 12.2522L13.5616 12.2522Z"
      )
      .attr("fill-rule", "evenodd")
      .attr("clip-rule", "evenodd");
    cross_join
      .append("path")
      .attr(
        "d",
        "M13.0053 14.2172L13.8382 14.248C13.8578 13.719 13.9392 13.1998 14.0796 12.698L13.2771 12.4734C13.119 13.0381 13.0274 13.6224 13.0053 14.2172L13.0053 14.2172ZM13.6941 17.6544L14.45 17.3035C14.2297 16.829 14.0661 16.3294 13.9628 15.8132L13.1457 15.9766C13.2617 16.5577 13.4462 17.1203 13.6941 17.6544H13.6941ZM15.881 20.4087L16.3947 19.7524C15.9816 19.429 15.6074 19.0579 15.2807 18.6474L14.6287 19.1663C14.9959 19.6279 15.4166 20.0451 15.881 20.4087V20.4087ZM19.1578 21.8799L19.3061 21.0596C18.7878 20.966 18.2853 20.8115 17.807 20.6L17.4699 21.3621C18.0083 21.6003 18.5743 21.7743 19.1578 21.8799H19.1578ZM22.4627 21.7404L22.2452 20.9358C21.7846 21.0604 21.3095 21.1353 20.8258 21.1586L20.8659 21.9909C21.4096 21.9649 21.9442 21.8806 22.4628 21.7404L22.4627 21.7404Z"
      )
      .attr("fill-rule", "evenodd")
      .attr("clip-rule", "evenodd");
    cross_join
      .append("path")
      .attr(
        "d",
        "M8.00015 19.4995C8.00015 23.6419 11.3582 26.9999 15.5005 26.9999C18.9535 26.9999 21.9361 24.6471 22.7719 21.3457L21.9641 21.1412C21.2213 24.0748 18.5698 26.1664 15.5006 26.1664C11.8186 26.1664 8.83351 23.1815 8.83351 19.4993C8.83351 16.4621 10.882 13.8312 13.7737 13.0579L13.5585 12.2528C10.3043 13.1228 8 16.0823 8 19.4992L8.00015 19.4995Z"
      )
      .attr("fill-rule", "evenodd")
      .attr("clip-rule", "evenodd");
    cross_join
      .append("path")
      .attr(
        "d",
        "M28.0012 14.4994C28.0012 10.357 24.6432 6.99902 20.5009 6.99902C17.1158 6.99902 14.1767 9.26084 13.2773 12.4732L14.0798 12.6978C14.879 9.84304 17.4918 7.83224 20.5008 7.83224C24.1828 7.83224 27.1679 10.8172 27.1679 14.4993C27.1679 17.5287 25.1296 20.1549 22.2481 20.9353L22.4658 21.7397C25.7086 20.8618 28.0014 17.9078 28.0014 14.4994L28.0012 14.4994Z"
      )
      .attr("fill-rule", "evenodd")
      .attr("clip-rule", "evenodd");
    cross_join
      .append("line")
      .attr("x1", 21.5)
      .attr("y1", 8)
      .attr("x2", 21.5)
      .attr("y2", 20.7279)
      .attr("stroke-dasharray", "2 2");
    cross_join
      .append("line")
      .attr("x1", 13.5)
      .attr("y1", 13)
      .attr("x2", 13.5)
      .attr("y2", 25.7279)
      .attr("stroke-dasharray", "2 2");
    cross_join
      .append("line")
      .attr("x1", 17.5)
      .attr("y1", 8)
      .attr("x2", 17.5)
      .attr("y2", 26)
      .attr("stroke-dasharray", "2 2");

    let inner_join = defs
      .append("g")
      .attr("id", "inner_join")
      .attr("viewBox", "0 0 35 35")
      .attr("width", 35)
      .attr("height", 35);
    inner_join
      .append("rect")
      .attr("x", 1)
      .attr("y", 1)
      .attr("width", 33)
      .attr("height", 33)
      .attr("rx", 16.5)
      .attr("fill", "white");
    inner_join
      .append("path")
      .attr(
        "d",
        "M7.443 15.829c-2.088 0-3.85-.719-5.288-2.156C.718 12.236 0 10.473 0 8.386c0-2.088.718-3.855 2.155-5.302 1.437-1.447 3.2-2.17 5.288-2.17.823 0 1.595.123 2.315.372.72.248 1.394.586 2.02 1.014-.178.17-.352.338-.52.504a3.5 3.5 0 00-.452.546 5.177 5.177 0 00-1.553-.829 5.688 5.688 0 00-1.81-.284c-1.694 0-3.138.602-4.33 1.804C1.92 5.244 1.324 6.692 1.324 8.386s.596 3.137 1.789 4.33c1.192 1.192 2.636 1.788 4.33 1.788.639 0 1.242-.09 1.81-.268a4.921 4.921 0 001.553-.814c.133.199.282.38.446.546.165.165.34.323.526.473-.6.447-1.268.79-2.003 1.03a7.501 7.501 0 01-2.332.358zm9.681 0c-.823 0-1.595-.12-2.315-.358a7.063 7.063 0 01-1.99-1.03c.158-.15.322-.308.49-.473.168-.165.319-.347.452-.546.452.363.964.634 1.538.814.574.179 1.182.268 1.825.268 1.694 0 3.142-.596 4.345-1.788 1.202-1.193 1.804-2.636 1.804-4.33s-.602-3.142-1.804-4.345c-1.203-1.202-2.65-1.804-4.345-1.804-.643 0-1.25.095-1.825.284a4.86 4.86 0 00-1.538.829 3.5 3.5 0 00-.452-.546 15.97 15.97 0 01-.49-.504 7.904 7.904 0 011.99-1.014 7.052 7.052 0 012.315-.373c2.088 0 3.855.724 5.302 2.17 1.447 1.448 2.17 3.215 2.17 5.303s-.723 3.85-2.17 5.287c-1.447 1.437-3.214 2.156-5.302 2.156zm-4.84-1.855a7.04 7.04 0 01-1.921-2.484 7.254 7.254 0 01-.682-3.104 7.331 7.331 0 012.602-5.618 7.5 7.5 0 011.936 2.514c.464.974.696 2.009.696 3.104 0 1.095-.232 2.13-.696 3.104a7.198 7.198 0 01-1.936 2.484z"
      )
      .attr("class", "transform")
      .attr("fill-rule", "evenodd")
      .attr("clip-rule", "evenodd");

    // group
    let group = defs
      .append("g")
      .attr("id", "group")
      .attr("viewBox", "0 0 35 35")
      .attr("width", 35)
      .attr("height", 35);
    group
      .append("rect")
      .attr("x", 1)
      .attr("y", 1)
      .attr("width", 33)
      .attr("height", 33)
      .attr("rx", 16.5)
      .attr("fill", "white");
    group
      .append("path")
      .attr(
        "d",
        "M11.0388 20.3138C10.833 20.2561 10.616 20.2253 10.3919 20.2253C9.07091 20.2253 8.00001 21.2962 8.00001 22.6172C8.00001 23.9382 9.07091 25.0091 10.3919 25.0091C11.5302 25.0091 12.4828 24.2139 12.7245 23.1487H21.3478C21.5895 24.2139 22.5421 25.0091 23.6804 25.0091C25.0014 25.0091 26.0723 23.9382 26.0723 22.6172C26.0723 21.2962 25.0014 20.2253 23.6804 20.2253C23.4562 20.2253 23.2393 20.2561 23.0335 20.3138L18.6169 12.1871C19.1144 11.7488 19.4282 11.107 19.4282 10.3919C19.4282 9.0709 18.3573 8 17.0363 8C15.7153 8 14.6444 9.0709 14.6444 10.3919C14.6444 11.107 14.9582 11.7489 15.4557 12.1871L11.0388 20.3138ZM16.3893 12.6953C16.5978 12.7536 16.8149 12.7839 17.0362 12.7839C17.2575 12.7839 17.4746 12.7536 17.6831 12.6953L22.0996 20.8216C21.7244 21.1521 21.4598 21.5936 21.348 22.0856H12.7244C12.6127 21.5936 12.3481 21.1521 11.9728 20.8216L16.3893 12.6953ZM17.0362 11.7208C16.3023 11.7208 15.7074 11.1259 15.7074 10.392C15.7074 9.65805 16.3023 9.06316 17.0362 9.06316C17.7701 9.06316 18.365 9.65805 18.365 10.392C18.365 11.1259 17.7701 11.7208 17.0362 11.7208ZM10.392 23.9461C9.65809 23.9461 9.0632 23.3512 9.0632 22.6173C9.0632 21.8833 9.65809 21.2884 10.392 21.2884C11.126 21.2884 11.7209 21.8833 11.7209 22.6173C11.7209 23.3512 11.126 23.9461 10.392 23.9461ZM23.6804 23.9461C22.9464 23.9461 22.3515 23.3512 22.3515 22.6173C22.3515 21.8833 22.9464 21.2884 23.6804 21.2884C24.4143 21.2884 25.0092 21.8833 25.0092 22.6173C25.0092 23.3512 24.4143 23.9461 23.6804 23.9461Z"
      );

    // sort
    let asc_sort = defs
      .append("g")
      .attr("id", "asc_sort")
      .attr("viewBox", "0 0 35 35")
      .attr("width", 35)
      .attr("height", 35);
    asc_sort
      .append("rect")
      .attr("x", 1)
      .attr("y", 1)
      .attr("width", 33)
      .attr("height", 33)
      .attr("rx", 16.5)
      .attr("fill", "white");
    asc_sort
      .append("path")
      .attr(
        "d",
        "M24 21.39V10.9998C24 10.6298 23.7016 10.3333 23.3334 10.3333C22.9626 10.3333 22.6666 10.6317 22.6666 10.9998V21.3932L22.3887 21.1153C22.1266 20.8531 21.7064 20.855 21.446 21.1154C21.1838 21.3776 21.1856 21.7978 21.4459 22.0581L22.8603 23.4725C23.0057 23.6178 23.1997 23.682 23.3881 23.666C23.5394 23.6531 23.6871 23.5884 23.803 23.4725L25.2174 22.0581C25.4796 21.796 25.4777 21.3758 25.2173 21.1154C24.9552 20.8532 24.5348 20.855 24.2746 21.1153L24 21.39ZM10.6666 11.0001C10.6666 10.6319 10.9659 10.3334 11.3305 10.3334H20.0028C20.3694 10.3334 20.6666 10.6293 20.6666 11.0001C20.6666 11.3683 20.3674 11.6667 20.0028 11.6667H11.3305C10.9638 11.6667 10.6666 11.3708 10.6666 11.0001ZM10.6666 15C10.6666 14.6318 10.9649 14.3334 11.3323 14.3334H18.6675C19.0351 14.3334 19.3332 14.6293 19.3332 15C19.3332 15.3682 19.0349 15.6667 18.6675 15.6667H11.3323C10.9647 15.6667 10.6666 15.3708 10.6666 15ZM10.6666 19C10.6666 18.6318 10.9631 18.3334 11.3303 18.3334H17.3363C17.7028 18.3334 18 18.6293 18 19C18 19.3682 17.7035 19.6667 17.3363 19.6667H11.3303C10.9638 19.6667 10.6666 19.3708 10.6666 19ZM10.6666 23C10.6666 22.6318 10.9648 22.3334 11.3313 22.3334H15.3353C15.7025 22.3334 16.0001 22.6293 16.0001 23C16.0001 23.3682 15.7018 23.6667 15.3353 23.6667H11.3313C10.9642 23.6667 10.6666 23.3708 10.6666 23Z"
      )
      .attr("fill-rule", "evenodd")
      .attr("clip-rule", "evenodd");

    let desc_sort = defs
      .append("g")
      .attr("id", "desc_sort")
      .attr("viewBox", "0 0 35 35")
      .attr("width", 35)
      .attr("height", 35);
    desc_sort
      .append("rect")
      .attr("x", 1)
      .attr("y", 1)
      .attr("width", 33)
      .attr("height", 33)
      .attr("rx", 16.5)
      .attr("fill", "white");
    desc_sort
      .append("path")
      .attr(
        "d",
        "M11 13.61L11 24.0002C11 24.3702 11.2984 24.6667 11.6666 24.6667C12.0374 24.6667 12.3334 24.3683 12.3334 24.0002L12.3334 13.6068L12.6113 13.8847C12.8734 14.1469 13.2936 14.145 13.554 13.8846C13.8162 13.6224 13.8144 13.2022 13.5541 12.9419L12.1397 11.5275C11.9943 11.3822 11.8003 11.318 11.6119 11.334C11.4606 11.3469 11.3129 11.4116 11.197 11.5275L9.78256 12.9419C9.5204 13.204 9.52229 13.6242 9.78267 13.8846C10.0448 14.1468 10.4652 14.145 10.7254 13.8847L11 13.61ZM24.3334 23.9999C24.3334 24.3681 24.0341 24.6666 23.6695 24.6666L14.9972 24.6666C14.6306 24.6666 14.3334 24.3707 14.3334 23.9999C14.3334 23.6317 14.6326 23.3333 14.9972 23.3333L23.6695 23.3333C24.0362 23.3333 24.3334 23.6292 24.3334 23.9999ZM24.3334 20C24.3334 20.3682 24.0351 20.6666 23.6677 20.6666L16.3325 20.6666C15.9649 20.6666 15.6668 20.3707 15.6668 20C15.6668 19.6318 15.9651 19.3333 16.3325 19.3333L23.6677 19.3333C24.0353 19.3333 24.3334 19.6292 24.3334 20ZM24.3334 16C24.3334 16.3682 24.0369 16.6666 23.6697 16.6666L17.6637 16.6666C17.2972 16.6666 17 16.3707 17 16C17 15.6318 17.2965 15.3333 17.6637 15.3333L23.6697 15.3333C24.0362 15.3333 24.3334 15.6292 24.3334 16ZM24.3334 12C24.3334 12.3682 24.0352 12.6666 23.6687 12.6666L19.6647 12.6666C19.2975 12.6666 18.9999 12.3707 18.9999 12C18.9999 11.6318 19.2982 11.3333 19.6647 11.3333L23.6687 11.3333C24.0358 11.3333 24.3334 11.6292 24.3334 12Z"
      )
      .attr("fill-rule", "evenodd")
      .attr("clip-rule", "evenodd");

    let both_sort = defs
      .append("g")
      .attr("id", "both_sort")
      .attr("viewBox", "0 0 35 35")
      .attr("width", 35)
      .attr("height", 35);
    both_sort
      .append("rect")
      .attr("x", 1)
      .attr("y", 1)
      .attr("width", 33)
      .attr("height", 33)
      .attr("rx", 16.5)
      .attr("fill", "white");
    both_sort
      .append("path")
      .attr(
        "d",
        "M27 22.3899V11.9996C27 11.6297 26.7016 11.3331 26.3334 11.3331C25.9626 11.3331 25.6666 11.6316 25.6666 11.9996V22.3931L25.3887 22.1152C25.1266 21.853 24.7064 21.8549 24.446 22.1153C24.1838 22.3774 24.1856 22.7976 24.4459 23.058L25.8603 24.4724C26.0057 24.6177 26.1997 24.6819 26.3881 24.6658C26.5394 24.653 26.6871 24.5883 26.803 24.4724L28.2174 23.058C28.4796 22.7959 28.4777 22.3756 28.2173 22.1153C27.9551 21.8531 27.5348 21.8549 27.2746 22.1152L27 22.3899ZM13.6665 11.9999C13.6665 11.6317 13.9659 11.3333 14.3305 11.3333H23.0028C23.3694 11.3333 23.6666 11.6292 23.6666 11.9999C23.6666 12.3681 23.3674 12.6666 23.0028 12.6666H14.3305C13.9638 12.6666 13.6665 12.3707 13.6665 11.9999ZM13.6665 15.9999C13.6665 15.6317 13.9649 15.3333 14.3323 15.3333H21.6675C22.0351 15.3333 22.3332 15.6292 22.3332 15.9999C22.3332 16.3681 22.0349 16.6666 21.6675 16.6666H14.3323C13.9647 16.6666 13.6665 16.3707 13.6665 15.9999ZM13.6665 19.9999C13.6665 19.6317 13.9631 19.3333 14.3303 19.3333H20.3363C20.7028 19.3333 21 19.6292 21 19.9999C21 20.3681 20.7034 20.6666 20.3363 20.6666H14.3303C13.9638 20.6666 13.6665 20.3707 13.6665 19.9999ZM13.6665 23.9999C13.6665 23.6317 13.9648 23.3333 14.3313 23.3333H18.3353C18.7025 23.3333 19 23.6292 19 23.9999C19 24.3681 18.7018 24.6665 18.3353 24.6665H14.3313C13.9642 24.6665 13.6665 24.3707 13.6665 23.9999Z"
      )
      .attr("fill-rule", "evenodd")
      .attr("clip-rule", "evenodd");
    both_sort
      .append("path")
      .attr(
        "d",
        "M8.41332 13.6098L8.41332 24.0001C8.41332 24.3701 8.71175 24.6666 9.07995 24.6666C9.45072 24.6666 9.7467 24.3682 9.7467 24.0001L9.7467 13.6067L10.0246 13.8846C10.2868 14.1468 10.707 14.1449 10.9673 13.8845C11.2295 13.6223 11.2277 13.2021 10.9675 12.9417L9.55305 11.5273C9.40763 11.382 9.21365 11.3179 9.02525 11.3339C8.87391 11.3468 8.72625 11.4115 8.61031 11.5273L7.19591 12.9417C6.93374 13.2039 6.93564 13.6241 7.19602 13.8845C7.45818 14.1466 7.87851 14.1449 8.13876 13.8846L8.41332 13.6098Z"
      )
      .attr("fill-rule", "evenodd")
      .attr("clip-rule", "evenodd");

    // physical table
    let physical_table = defs
      .append("g")
      .attr("id", "physical_table")
      .attr("viewBox", "0 0 21 19")
      .attr("width", 21)
      .attr("height", 19);
    physical_table
      .append("rect")
      .attr("x", 8)
      .attr("y", 5.83325)
      .attr("width", 7.8)
      .attr("height", 0.433333)
      .attr("rx", 0.216667)
      .attr("stroke", "none");
    physical_table
      .append("rect")
      .attr("x", 8)
      .attr("y", 6.69995)
      .attr("width", 1.73333)
      .attr("height", 5.63333)
      .attr("rx", 0.866667)
      .attr("stroke", "none");
    physical_table
      .append("rect")
      .attr("x", 10.6)
      .attr("y", 6.69995)
      .attr("width", 5.2)
      .attr("height", 5.63333)
      .attr("rx", 1)
      .attr("stroke", "none");

    let inline_table = defs
      .append("g")
      .attr("id", "inline_table")
      .attr("viewBox", "0 0 21 19")
      .attr("width", 21)
      .attr("height", 19);
    inline_table
      .append("path")
      .attr(
        "d",
        "M14.733 10.7997C14.4863 10.7127 14.219 10.7032 13.9668 10.7726C13.7846 10.8187 13.5914 10.7799 13.4411 10.6671L13.3331 10.5884C13.2382 10.5223 13.1631 10.4316 13.116 10.326C13.0688 10.2204 13.0513 10.104 13.0654 9.98921C13.077 9.90122 13.0828 9.81266 13.0826 9.72392C13.0825 9.61611 13.0735 9.50848 13.0556 9.40223C13.0383 9.28851 13.0904 9.17545 13.1882 9.11484L13.2348 9.0854C13.3145 9.03129 13.4161 9.0211 13.505 9.05835C13.7924 9.1784 14.1136 9.18975 14.4089 9.09025C14.8162 8.9572 15.1354 8.63739 15.2678 8.22986C15.4002 7.82224 15.3298 7.37598 15.0783 7.02894C14.8269 6.68185 14.4247 6.47586 13.9962 6.47462C13.6389 6.4733 13.296 6.61565 13.0447 6.86975C12.7935 7.12376 12.6549 7.46816 12.6602 7.82544C12.6613 7.92535 12.61 8.01852 12.5251 8.07107C12.4296 8.13348 12.3062 8.13348 12.2107 8.07107C11.8776 7.84443 11.4839 7.72372 11.0809 7.7248H10.9238C10.6376 7.75078 10.3585 7.62612 10.187 7.39562L10.0961 7.28756C9.95821 7.08856 9.91093 6.84056 9.96594 6.60478C10.0136 6.3784 9.99924 6.14338 9.92416 5.92449C9.79095 5.51687 9.47091 5.19756 9.06295 5.06534C8.655 4.93311 8.20847 5.00399 7.86146 5.25603C7.51454 5.508 7.30903 5.91077 7.30853 6.33961C7.30762 6.66797 7.42793 6.98513 7.64642 7.23027C7.86483 7.47542 8.16606 7.63141 8.49235 7.66833C8.72952 7.69185 8.94513 7.81618 9.08426 8.00968L9.15547 8.11042C9.3247 8.35562 9.35266 8.67181 9.22915 8.94293C9.02628 9.41815 9.01567 9.95349 9.19963 10.4362C9.23811 10.5447 9.2465 10.6615 9.22405 10.7743C9.20152 10.8871 9.14897 10.9918 9.07192 11.0773L9.01296 11.1435H9.01304C8.86651 11.3069 8.6481 11.3861 8.43093 11.3548C8.21467 11.3276 7.99502 11.3555 7.7924 11.4358C7.40139 11.585 7.10254 11.9083 6.9843 12.3098C6.86613 12.7112 6.94203 13.1448 7.18971 13.4821C7.4374 13.8194 7.82833 14.0218 8.24673 14.0293C8.71116 14.0199 9.13968 13.7771 9.3863 13.3834C9.54567 13.1608 9.62443 12.8906 9.6098 12.6171C9.60388 12.4561 9.6612 12.2992 9.76942 12.18L10.015 11.8927V11.8926C10.0888 11.8062 10.1847 11.7414 10.2923 11.705C10.4001 11.6687 10.5155 11.6623 10.6266 11.6864C10.7733 11.7193 10.9232 11.7358 11.0736 11.7354C11.3766 11.7358 11.6756 11.6669 11.9479 11.5341C12.0523 11.4863 12.1678 11.4677 12.2819 11.4802C12.3961 11.4928 12.5047 11.536 12.5963 11.6053L12.6798 11.6667C12.8427 11.7799 12.9463 11.9601 12.9622 12.1579C12.9854 12.5253 13.1586 12.8671 13.4412 13.103C13.7239 13.3389 14.0911 13.4483 14.4567 13.4053C14.8224 13.3625 15.1544 13.1711 15.3747 12.8763C15.5951 12.5814 15.6845 12.2088 15.6221 11.846C15.5809 11.608 15.4767 11.3855 15.3204 11.2014C15.164 11.0175 14.9612 10.8787 14.733 10.7997L14.733 10.7997Z"
      )
      .attr("stroke", "none");

    let common_table = defs
      .append("g")
      .attr("id", "common_table")
      .attr("viewBox", "0 0 21 19")
      .attr("width", 21)
      .attr("height", 19);
    common_table
      .append("path")
      .attr(
        "d",
        "M10.2117 6.80609C10.2117 7.69292 9.49272 8.41188 8.60589 8.41188C7.71896 8.41188 7 7.69292 7 6.80609C7 5.91915 7.71896 5.2002 8.60589 5.2002C9.49272 5.2002 10.2117 5.91915 10.2117 6.80609Z"
      )
      .attr("stroke", "none");
    common_table
      .append("path")
      .attr(
        "d",
        "M10.2117 12.3942C10.2117 13.2812 9.49272 14.0001 8.60589 14.0001C7.71896 14.0001 7 13.2812 7 12.3942C7 11.5074 7.71896 10.7885 8.60589 10.7885C9.49272 10.7885 10.2117 11.5074 10.2117 12.3942Z"
      )
      .attr("stroke", "none");
    common_table
      .append("path")
      .attr(
        "d",
        "M15.8 6.80609C15.8 7.69292 15.081 8.41188 14.1941 8.41188C13.3072 8.41188 12.5883 7.69292 12.5883 6.80609C12.5883 5.91915 13.3072 5.2002 14.1941 5.2002C15.081 5.2002 15.8 5.91915 15.8 6.80609Z"
      )
      .attr("stroke", "none");
    common_table
      .append("path")
      .attr(
        "d",
        "M15.8 12.3942C15.8 13.2812 15.081 14.0001 14.1941 14.0001C13.3072 14.0001 12.5883 13.2812 12.5883 12.3942C12.5883 11.5074 13.3072 10.7885 14.1941 10.7885C15.081 10.7885 15.8 11.5074 15.8 12.3942Z"
      )
      .attr("stroke", "none");

    let function_table = defs
      .append("g")
      .attr("id", "function_table")
      .attr("viewBox", "0 0 21 19")
      .attr("width", 21)
      .attr("height", 19);
    function_table
      .append("path")
      .attr(
        "d",
        "M14.6641 7.60156H15.3672C15.4867 7.60156 15.5781 7.50312 15.5781 7.39062C15.5781 7.37656 15.5781 7.36953 15.5781 7.3625C15.5711 6.93359 15.4023 6.53281 15.093 6.23047C14.8258 5.97031 14.4742 5.82266 14.1086 5.78047L13.9609 5.77344H9.03906C8.53281 5.77344 8.125 6.18125 8.125 6.6875V7.16562C8.125 7.27812 8.20937 7.38359 8.32187 7.39062C8.44141 7.39766 8.54688 7.29922 8.54688 7.17969V6.6875C8.54688 6.41328 8.76484 6.19531 9.03906 6.19531H13.75V6.6875C13.75 7.19375 14.1578 7.60156 14.6641 7.60156ZM14.1719 6.21641C14.4109 6.25859 14.6289 6.37109 14.8047 6.54688C14.9805 6.72266 15.093 6.94062 15.1352 7.17969H14.6641C14.3898 7.17969 14.1719 6.96172 14.1719 6.6875V6.21641ZM15.325 8.31172C15.2266 8.33281 15.1562 8.42422 15.1562 8.52969V12.875C15.1562 13.0367 15.0156 13.1703 14.8469 13.1562C14.6992 13.1422 14.5938 13.0086 14.5938 12.8609V9.35938C14.5938 9.05 14.3406 8.79688 14.0312 8.79688H11.725C11.4227 8.79688 11.1344 8.64922 10.9586 8.41016C10.7055 8.06562 10.2906 7.88281 9.86172 7.88281H7.98438C7.675 7.88281 7.42188 8.13594 7.42188 8.44531V13.0156C7.42188 13.325 7.675 13.5781 7.98438 13.5781H14.8961C14.9031 13.5781 14.9172 13.5781 14.9242 13.5781C15.2898 13.55 15.5781 13.2477 15.5781 12.875V8.51562C15.5781 8.38203 15.4586 8.28359 15.325 8.31172Z"
      )
      .attr("stroke", "none");

    let recursive_table = defs
      .append("g")
      .attr("id", "recursive_table")
      .attr("viewBox", "0 0 21 19")
      .attr("width", 21)
      .attr("height", 19);
    recursive_table
      .append("path")
      .attr(
        "d",
        "M16.1379 6.32875C16.263 6.45376 16.3332 6.62341 16.3332 6.8002C16.3332 6.97698 16.263 7.14654 16.1379 7.27164C16.0129 7.39664 15.8434 7.46691 15.6666 7.46691H14.1998V13.7336C14.1998 13.9718 14.0728 14.1919 13.8665 14.311C13.6602 14.4301 13.4061 14.4301 13.1998 14.311C12.9936 14.1919 12.8665 13.9718 12.8665 13.7336V4.66683C12.8665 4.48995 12.9367 4.3204 13.0618 4.19539C13.1868 4.07038 13.3563 4.00011 13.5331 4.00011C13.71 4.00011 13.8796 4.07038 14.0046 4.19539L16.1379 6.32875ZM8.4664 4C8.28952 4 8.11997 4.07027 7.99496 4.19527C7.86995 4.32028 7.79969 4.48984 7.79969 4.66672V10.9334H6.33295C6.15615 10.9334 5.9866 11.0037 5.8616 11.1287C5.7365 11.2538 5.66632 11.4233 5.66632 11.6001C5.66632 11.7769 5.7365 11.9466 5.8616 12.0716L7.99496 14.2049C8.11996 14.3299 8.28953 14.4002 8.4664 14.4002C8.64319 14.4002 8.81274 14.3299 8.93775 14.2049C9.06285 14.0799 9.13303 13.9104 9.13303 13.7335V4.66672C9.13303 4.48984 9.06285 4.32028 8.93775 4.19527C8.81275 4.07027 8.64318 4 8.4664 4Z"
      )
      .attr("stroke", "none");

    let pseudo_table = defs
      .append("g")
      .attr("id", "pseudo_table")
      .attr("viewBox", "0 0 21 19")
      .attr("width", 21)
      .attr("height", 19);
    pseudo_table
      .append("path")
      .attr(
        "d",
        "M11.816 12.8419C10.8444 12.7768 9.93532 12.3394 9.2782 11.6208C8.62097 10.9023 8.26632 9.95781 8.28803 8.98428C8.30975 8.01075 8.70616 7.08302 9.39473 6.39457C10.0833 5.70611 11.011 5.30956 11.9844 5.28787C12.9579 5.26618 13.9025 5.62081 14.621 6.27804C15.3396 6.93518 15.777 7.84428 15.8421 8.81587C15.8542 8.95453 15.9706 9.06078 16.1098 9.06023H16.8603C17.0081 9.06023 17.128 8.94041 17.128 8.79255C17.0567 7.48229 16.4799 6.25061 15.519 5.3569C14.5582 4.46323 13.2881 3.97708 11.976 4.00083C10.6639 4.02467 9.41231 4.55646 8.48431 5.4843C7.55644 6.41226 7.02467 7.66384 7.00083 8.97596C6.97708 10.288 7.46323 11.5581 8.3569 12.519C9.25066 13.4799 10.4822 14.0567 11.7926 14.128C11.8635 14.128 11.9317 14.0998 11.9818 14.0496C12.032 13.9993 12.0602 13.9313 12.0602 13.8603V13.1098C12.0623 12.9695 11.9557 12.8514 11.8159 12.8392L11.816 12.8419Z"
      )
      .attr("stroke", "none");
    pseudo_table
      .append("path")
      .attr(
        "d",
        "M16.8604 13.0602C16.9846 13.186 16.9846 13.3882 16.8604 13.514L16.5229 13.8602C16.4622 13.9237 16.378 13.9595 16.2902 13.9591C16.2032 13.9594 16.12 13.9236 16.0603 13.8602L14.9927 12.7925L13.928 13.8602C13.8677 13.9201 13.7861 13.9536 13.7011 13.9533C13.616 13.9543 13.5341 13.9206 13.4742 13.8602L13.128 13.5228C13.0644 13.4621 13.0286 13.3779 13.0291 13.29C13.0288 13.203 13.0646 13.1198 13.128 13.0602L14.1927 11.9925L13.128 10.9278V10.9278C13.0672 10.8678 13.0327 10.7862 13.0319 10.7009C13.0321 10.6154 13.0667 10.5336 13.128 10.474L13.4654 10.1278C13.5251 10.0644 13.6083 10.0286 13.6953 10.0289C13.7831 10.0285 13.8673 10.0643 13.928 10.1278L14.9928 11.1926L16.0604 10.1278H16.0603C16.12 10.0667 16.2019 10.0322 16.2872 10.0322C16.3727 10.0322 16.4545 10.0667 16.5142 10.1278L16.8604 10.4653C16.9237 10.5249 16.9596 10.6081 16.9593 10.6951C16.9596 10.7829 16.9238 10.8671 16.8604 10.9279L15.7927 11.9926L16.8604 13.0602Z"
      )
      .attr("stroke", "none");

    let select = defs
      .append("g")
      .attr("id", "select")
      .attr("viewBox", "0 0 21 19")
      .attr("width", 11)
      .attr("height", 10);
    select
      .append("path")
      .attr(
        "d",
        "M27.5246 13.1742L1.55511 13.1742C1.60255 11.4429 1.97608 9.80055 2.67571 8.24713C3.37535 6.6937 4.30621 5.3478 5.46832 4.20941C6.63042 3.07102 7.98818 2.17573 9.54161 1.52353C11.095 0.871328 12.7611 0.545228 14.5398 0.545227C16.2948 0.545227 17.955 0.871328 19.5203 1.52353C21.0856 2.17573 22.4492 3.07102 23.6114 4.20941C24.7735 5.3478 25.7043 6.6937 26.404 8.24712C27.1036 9.80055 27.4771 11.4429 27.5246 13.1742Z"
      )
      .attr("stroke", "none");
    select
      .append("rect")
      .attr("x", 9.46765)
      .attr("y", 14.9216)
      .attr("width", 4.04513)
      .attr("height", 4.04513)
      .attr("fill", "#A53FF6");
    select
      .append("rect")
      .attr("x", 4.60385)
      .attr("y", 15.4995)
      .attr("width", 2.88938)
      .attr("height", 2.88938)
      .attr("fill", "#D29FFB");
    select
      .append("rect")
      .attr("x", 5.18356)
      .attr("y", 21.8579)
      .attr("width", 1.73)
      .attr("height", 1.73)
      .attr("fill", "#D29FFB");
    select
      .append("rect")
      .attr("x", 10.0455)
      .attr("y", 21.2782)
      .attr("width", 2.88938)
      .attr("height", 2.88938)
      .attr("fill", "#A53FF6");
    select
      .append("rect")
      .attr("x", 16.0651)
      .attr("y", 21.2782)
      .attr("width", 2.88938)
      .attr("height", 2.88938)
      .attr("fill", "#D29FFB");
    select
      .append("rect")
      .attr("x", 0.895844)
      .attr("y", 16.0773)
      .attr("width", 1.73363)
      .attr("height", 1.73363)
      .attr("fill", "#D29FFB");
    select
      .append("rect")
      .attr("x", 10.6234)
      .attr("y", 26.0348)
      .attr("width", 1.73363)
      .attr("height", 1.73363)
      .attr("fill", "#A53FF6");
    select
      .append("rect")
      .attr("x", 16.6429)
      .attr("y", 26.0348)
      .attr("width", 1.73363)
      .attr("height", 1.73363)
      .attr("fill", "#D29FFB");
    select
      .append("rect")
      .attr("x", 26.3705)
      .attr("y", 16.0773)
      .attr("width", 1.73363)
      .attr("height", 1.73363)
      .attr("fill", "#D29FFB");
    select
      .append("rect")
      .attr("x", 21.5067)
      .attr("y", 15.4995)
      .attr("width", 2.88938)
      .attr("height", 2.88938)
      .attr("fill", "#A53FF6");
    select
      .append("rect")
      .attr("x", 22.0864)
      .attr("y", 21.8579)
      .attr("width", 1.73)
      .attr("height", 1.73)
      .attr("fill", "#A53FF6");
    select
      .append("rect")
      .attr("x", 15.4872)
      .attr("y", 14.9216)
      .attr("width", 4.04513)
      .attr("height", 4.04513)
      .attr("fill", "#D29FFB");

    // semi_join
    const semi_join = defs
      .append("g")
      .attr("id", "semi_join")
      .attr("viewBox", "0 0 35 35")
      .attr("width", 35)
      .attr("height", 35);
    semi_join
      .append("rect")
      .attr("x", 1)
      .attr("y", 1)
      .attr("width", 33)
      .attr("height", 33)
      .attr("rx", 16.5)
      .attr("fill", "white");
    semi_join
      .append("path")
      .attr(
        "d",
        "M15.0976 6.44487C15.0388 6.39935 14.9733 6.35911 14.901 6.32536L1.91394 0.263802C1.25096 -0.0456388 0.491003 0.438315 0.491003 1.16996L0.491002 13.2931C0.491002 14.0247 1.25095 14.5087 1.91394 14.1992L7.05234 11.8009C7.10311 11.791 7.1535 11.7747 7.20249 11.7519L15.2984 7.97322L22.8791 11.5115C23.2044 11.6633 23.5912 11.5227 23.7431 11.1974C23.8949 10.8721 23.7543 10.4853 23.429 10.3334L16.8352 7.2559L23.554 4.12002C23.8793 3.96819 24.0199 3.58141 23.8681 3.25611C23.7162 2.93081 23.3294 2.79019 23.0041 2.94202L15.2984 6.53859L15.0976 6.44487Z"
      )
      .attr("fill", "black")
      .attr("class", "transform-semi-join");

    // anti_join
    const anti_join = defs
      .append("g")
      .attr("id", "anti_join")
      .attr("viewBox", "0 0 35 35")
      .attr("width", 35)
      .attr("height", 35);
    anti_join
      .append("rect")
      .attr("x", 1)
      .attr("y", 1)
      .attr("width", 33)
      .attr("height", 33)
      .attr("rx", 16.5)
      .attr("fill", "white");
    anti_join
      .append("path")
      .attr(
        "d",
        "M14.901 6.45084C15.6704 6.80995 15.6704 7.90404 14.901 8.26315L1.91392 14.3247C1.25094 14.6341 0.490987 14.1502 0.490987 13.4185L0.490988 1.29544C0.490988 0.563798 1.25094 0.0798447 1.91393 0.389285"
      )
      .attr("fill", "black")
      .attr("class", "transform-anti-join");
  };

  const updateScaleNotify = (scale: number) => {
    d3.select<HTMLDivElement, unknown>(".scale-notify-box").text(
      Math.round(scale * 100) + "%"
    );
  };

  // create diagram elements
  const createElements = () => {
    if (!diagramRef.current) {
      throw new Error(
        "Cannot createElements because diagram is not yet mounted"
      );
    }
    const container = d3.select<SVGGElement, unknown>(diagramRef.current);

    if (!chartData) {
      throw new Error("Cannot createElements because chartData is undefined");
    }
    if (!chartData.edges || !chartData.nodes) {
      throw new Error(
        "Cannot createElements because edges or nodes are undefined"
      );
    }
    container
      .selectAll(".edge")
      .data(chartData["edges"])
      .enter()
      .call(createEdge);

    container
      .selectAll(".node")
      .data(chartData["nodes"])
      .enter()
      .call(createRecursiveEdge);

    container
      .selectAll(".node")
      .data(chartData["nodes"])
      .enter()
      .call(createNode);

    container
      .selectAll(".notify")
      .data(chartData["nodes"])
      .enter()
      .call(createNotify);
  };

  /**
   * Insert the operation symbol into the diagram
   */
  const insertOperation = (
    selection: d3.Selection<d3.EnterElement, Node, SVGGElement, unknown>
  ) => {
    /////////////// insert operation ///////////////
    const operator_group = selection
      .filter((d) => d.type === "operation" && d.hidden === false)
      .append("g")
      .attr("class", (d) => (d.unused ? "node unused" : "node"))
      .attr("transform", (d) => "translate(" + d.x + "," + d.y + ")")
      .attr("id", (d) => d.id)
      .on("mouseenter", (event: React.MouseEvent<SVGGElement>) =>
        showTooltip(event.target as SVGGElement)
      )
      .on("mouseleave", (event: React.MouseEvent<SVGGElement>) =>
        hideTooltip(event.target as SVGGElement)
      )
      .on("click", handleClickedNode);
    operator_group
      .append("use")
      .attr(
        "transform",
        "translate(-" +
          (activeDiagram.operatorRadius || 15) +
          ",-" +
          (activeDiagram.operatorRadius || 15) +
          ") scale(" +
          (activeDiagram && activeDiagram.operatorRadius
            ? activeDiagram.operatorRadius
            : 15) /
            17 +
          ")"
      )
      .attr("xlink:href", (d) =>
        linkMap[d.subtype as keyof typeof linkMap]
          ? linkMap[d.subtype as keyof typeof linkMap]
          : "#join"
      )
      .attr("class", "operator");

    operator_group
      .append("text")
      .attr("class", "operator-label")
      .attr("stroke", "none")
      .attr(
        "x",
        (d) =>
          (-d.label.length === -6
            ? (-d.label.length * 12) / 2
            : -d.label.length === -5
            ? ((-d.label.length - 2) * 13) / 2
            : -d.label.length * cCharWidth) / 2
      )
      .attr(
        "y",
        (activeDiagram.operatorRadius ? activeDiagram.operatorRadius : 15) + 20
      )
      .text((d) => d.label);
  };

  // create Nodes for elements consists of chart.
  const createNode = (
    selection: d3.Selection<d3.EnterElement, Node, SVGGElement, unknown>
  ) => {
    /////////////// insert node ///////////////
    const node_group = selection
      .filter((d) => d.type === "dataset" && d.hidden === false)
      .append("g")
      .attr("class", (d) => (d.unused ? "node unused" : "node"))
      .attr("id", (d) => d.id)
      .attr("transform", (d) => "translate(" + d.x + "," + d.y + ")");

    const dataset_group = node_group
      .append("g")
      .attr("class", (d) => "dataset " + d.subtype)
      .attr("width", (d) => d.width)
      .attr("height", (d) => d.height)
      .attr(
        "transform",
        (d) => "translate(" + -d.width / 2 + "," + -d.height / 2 + ")"
      );

    // create up/downstream handle.
    dataset_group.call(createCollapseHandle);

    dataset_group
      .append("rect")
      .attr("class", "dataset-body")
      .attr("x", 0)
      .attr("y", 0)
      .attr("rx", 10)
      .attr("ry", 10)
      .attr("width", (d) => d.width)
      .attr("height", (d) => {
        let staticHeight = 43;
        if (d.refdb !== null) {
          staticHeight = staticHeight + 30;
        }
        if (d.refsch !== null) {
          staticHeight = staticHeight + 30;
        }
        if (d.collapse) {
          //setting default height
          return 40;
        } else {
          //25 is one item height
          return staticHeight + 25 * (d.columns?.length || 0);
        }
      });

    dataset_group
      .append("path")
      .attr("d", (d) =>
        makeHeaderRect(
          0.5,
          0.5,
          d.width - 2,
          cTitleHeight - 1,
          10,
          d.columns !== undefined && d.columns.length > 0 && !d.collapse
        )
      )
      .attr("class", "frame dataset-header")
      .on("mouseenter", (event, d) => {
        if (d.collapse) {
          showTooltip(event.target.parentNode.parentNode);
        }
      })
      .on("mouseleave", (event) =>
        hideTooltip(event.target.parentNode.parentNode)
      )
      .on("click", handleClickedNode);

    dataset_group
      .append("path")
      .attr("d", (d) =>
        makeIconRect(
          0.5,
          0.5,
          cTitleHeight - 1,
          cTitleHeight - 1,
          10,
          d.columns !== undefined && d.columns.length > 0 && !d.collapse
        )
      )
      .attr("class", "frame dataset-icon-box");

    dataset_group
      .append("path")
      .attr("d", "M" + (cTitleHeight - 0.5) + ",0V" + cTitleHeight)
      .attr("class", "dataset-icon-split-line");

    dataset_group
      .append("use")
      .attr("transform", (d) =>
        linkMap[d.subtype as keyof typeof linkMap] === "#select"
          ? "translate(8, 9)scale(0.8)"
          : "scale(" + cTitleHeight / 20 + ")"
      )
      .attr("xlink:href", (d) =>
        linkMap[d.subtype as keyof typeof linkMap]
          ? linkMap[d.subtype as keyof typeof linkMap]
          : "#physical_table"
      )
      .attr("class", "dataset-icon");

    dataset_group
      .append("text")
      .attr("stroke", "none")
      .attr("x", cTitleHeight + 10)
      .attr("y", cTitleHeight / 2 + 5)
      .text((d) =>
        d.label.length < 25 ? d.label : d.label.substr(0, 23) + "..."
      )
      .attr("class", "dataset-label");

    // insert detail view
    const detailView = dataset_group
      .filter((d) => d.columns !== undefined && d.columns.length > 0)
      .append("g")
      .attr("transform", "translate(0," + cTitleHeight + ")")
      .attr(
        "class",
        (d) => "dataset-detail-view" + (d.collapse ? " fade" : "")
      );

    detailView
      .append("rect")
      .attr("width", (d) => d.width)
      .attr("height", (d) => {
        if (d.collapse) {
          return 0;
        } else {
          //25 is one item height
          return 25 * (d.columns?.length || 0);
        }
      })
      .attr("class", (d) => "detail-wrapper" + (d.collapse ? " hidden" : ""))
      .on("click", handleClickedNode);

    detailView
      .append("path")
      .attr("d", (d) => makeDetailSplitLines(d, true))
      .attr("class", "detail-split-line");

    detailView
      .append("text")
      .text((d) =>
        d.refdb && d.refsch == null
          ? "Schema:   " + d.refdb
          : "Catalog:  " + d.refdb
      )
      .attr("x", "15px")
      .attr("y", "18px")
      .attr("class", (d) => "detail-label" + (!d.refdb ? " hidden" : ""));

    detailView
      .append("text")
      .text((d) => "Schema:   " + (d.refsch ? d.refsch : ""))
      .attr("x", "15px")
      .attr("y", "48px")
      .attr("class", (d) => "detail-label" + (!d.refsch ? " hidden" : ""));

    const columns = detailView
      .append("foreignObject")
      .on("click", handleClickedNode)
      .attr("class", "detail-column")
      .attr("width", (d) => d.width - 5)
      .attr("height", (d) => {
        if (d.collapse) {
          return 0;
        } else {
          //25 is one item height
          return 25 * (d.columns?.length || 0);
        }
      })
      .attr("y", (d) =>
        d.refdb && d.refsch ? "60px" : d.refdb || d.refsch ? "30px" : "0px"
      )
      .append("xhtml:textarea")
      .attr("class", "detail-column-text")
      .attr("readonly", "");

    columns.each((d, i, selection) => {
      let text = "";
      if (d.columns) {
        d.columns.forEach((column, index) => {
          text += " " + column.toString();
          if (
            index !==
            (d as SetRequired<Node, "columns">).columns.length - 1
          ) {
            text += "\r\n";
          }
        });
      }
      const targetSelection = selection[i] as
        | (Element & { value: string })
        | null;
      if (targetSelection) {
        targetSelection.value = text;
        targetSelection.innerHTML = text;
      }
    });
    insertOperation(selection);
    // create detailview handle
    dataset_group.call(createDetailViewHandle);
  };

  // create edges between nodes
  const createEdge = (
    selection: d3.Selection<d3.EnterElement, Edge, SVGGElement, unknown>
  ) => {
    selection
      .filter((d) => !d.hidden)
      .append("g")
      .attr("id", (d) => d.id)
      .append("path")
      .attr("d", (d) => line(makeEdge(d)))
      .attr("class", "path edge");
  };

  // create recursive edge
  const createRecursiveEdge = (
    selection: d3.Selection<d3.EnterElement, Node, SVGGElement, unknown>
  ) => {
    selection
      .filter((d: Node) => d.recursiveCteTargetId !== null)
      .append("g")
      .attr("class", (d) =>
        d.unused ? "recursive-edge unused" : "recursive-edge"
      )
      .append("path")
      .attr("d", (d) =>
        line(makeRecursiveEdge(d as SetRequired<Node, "recursiveCteTargetId">))
      )
      .attr("class", "recursive-path");
  };

  // create handle to collapse / expands chart
  const createCollapseHandle = (
    selection: d3.Selection<SVGGElement, Node, SVGGElement, unknown>
  ) => {
    const handle_group = selection
      .filter((d) => canHaveCollapseHandle(d))
      .append("g")
      .attr("class", "collapse-handle")
      .attr("id", (d) => "handle_collapse_" + d.id)
      .attr("transform", (d) => calcCollapseHandlePosition(d))
      .on("click", (event, d) => handleUpstream(event, d));

    handle_group
      .append("line")
      .attr("class", (d) =>
        d.stream_expanded ? "handle-connector hidden" : "handle-connector"
      )
      .attr("x1", 14)
      .attr("y1", 0)
      .attr("x2", activeDiagram.direction === "LR" ? 45 : 14)
      .attr("y2", activeDiagram.direction === "LR" ? 0 : 30);

    handle_group
      .append("use")
      .attr("class", "arrow")
      .attr("xlink:href", (d) => {
        return d.stream_expanded ? "#up_stream" : "#down_stream";
      });

    handle_group
      .append("rect")
      .attr("width", 32)
      .attr("height", 32)
      .attr("y", -16)
      .attr("class", "haddle-near");
  };

  // check if the dataset can have upstream handle
  const canHaveCollapseHandle = (d: Node) => {
    let ret = d.type === "dataset" && d.subtype !== "PHYSICAL";
    let count = 0;
    if (chartData && chartData.edges) {
      chartData.edges.forEach((edge) => {
        if (edge.target === d.id) count++;
      });
    }
    return ret && count > 0;
  };

  // calculate collapse handle position
  const calcCollapseHandlePosition = (d: Node) => {
    let offset_x = -25;
    let offset_y = d.height / 2;
    if (activeDiagram.direction !== "LR") {
      offset_x = d.width / 2 - 14;
      offset_y = -30;
    }
    return "translate(" + offset_x + "," + offset_y + ")";
  };

  // create handle to show / hide detail View box
  const createDetailViewHandle = (
    selection: d3.Selection<SVGGElement, Node, SVGGElement, unknown>
  ) => {
    const handle_group = selection
      .filter((d) => canHaveDetailViewHandle(d))
      .append("g")
      .attr("class", "detailview-handle")
      .attr("transform", (d) => calcDetailViewHandlePosition(d))
      .on("click", (event, d) => handleDetailView(event, d));

    handle_group
      .append("use")
      .attr("transform", "translate(12,-6) scale(2)")
      .attr("class", "arrow ")
      .attr("xlink:href", (d) => {
        return d.collapse ? "#expand_down" : "#collapse_up";
      });

    handle_group
      .append("rect")
      .attr("width", 28)
      .attr("height", 32)
      .attr("x", 8)
      .attr("y", -16)
      .attr("class", "haddle-near");
  };

  // check if the dataset can have detail view handle
  const canHaveDetailViewHandle = (d: Node) => {
    return (
      d.type === "dataset" && d.columns !== undefined && d.columns.length > 0
    );
  };

  // draw detail view
  const makeDetailSplitLines = (d: Node, removeEmptyRows = false) => {
    return (
      "M1,0H" +
      (d.width - 2) +
      (!removeEmptyRows || d.refdb || d.refsch
        ? "M1,30H" + (d.width - 2)
        : "") +
      (!removeEmptyRows || (d.refdb && d.refsch)
        ? "M1,60H" + (d.width - 2)
        : "")
    );
  };

  // calculate detail view handle position
  const calcDetailViewHandlePosition = (d: Node) => {
    const offset_x = d.width - 36;
    const offset_y = d.collapse ? d.height / 2 : cTitleHeight / 2;
    return "translate(" + offset_x + "," + offset_y + ")";
  };

  // create tooltip for node
  const createNotify = (
    selection: d3.Selection<d3.EnterElement, Node, SVGGElement, unknown>
  ) => {
    const node_group = selection
      .filter((d) => d.hidden === false)
      .append("g")
      .attr("class", "notify")
      .attr("transform", (d) => "translate(" + d.x + "," + d.y + ")");

    const notify_group = node_group
      .filter(
        (d) =>
          (d.type === "operation" || d.columns !== undefined) &&
          d.columns !== undefined &&
          d.columns.length > 0
      )
      .append("g")
      .attr("id", (d) => "notify_" + d.id)
      .attr("display", "none")
      .attr("transform", (_) => "translate(25, " + cCharHeight + ")");

    notify_group
      .append("rect")
      .attr("width", (d) => {
        let max = d.columns !== undefined ? d.columns[0].length : 0;
        if (d.columns) {
          d.columns.forEach((e) => {
            if (e.length > max) max = e.length;
          });
        }
        return max * cCharWidth + 70;
      })
      .attr("height", (d) => 20 + (d.columns?.length || 0) * cCharHeight)
      .attr("x", 0)
      .attr("y", 0)
      .attr("rx", 4)
      .attr("ry", 4)
      .attr("class", "notify-panel");

    const node_hidden_columns = notify_group
      .append("text")
      .attr("class", "tooltip-text")
      .attr("stroke", "none")
      .attr("x", 0)
      .attr("y", 7);

    node_hidden_columns.each((d, i, selection) => {
      const select = d3.select(selection[i]).selectAll("tspan");
      select
        .data(d.columns || [])
        .enter()
        .append("tspan")
        .attr("x", 20)
        .attr("dy", cCharHeight)
        .text((d) => d.toString());
    });
  };

  // show tooltip
  const showTooltip = (elem: SVGGElement) => {
    d3.select("#notify_" + elem.id).attr("style", "display: block");
  };

  // hide tooltip
  const hideTooltip = (elem: SVGGElement) => {
    const element = document.querySelector("#notify_" + elem.id);

    var display = element?.getAttribute("display");
    // Get if the tooltip is not open by click on operator
    if (display !== "block") {
      d3.select("#notify_" + elem.id).attr("style", "display: none");
    }
  };

  const handleStreamDiagram = () => {
    if (!streamID || !streamID.length) {
      return;
    }
    streamID.forEach((d) => {
      const dataTarget = Object.values(d)[0];
      setTimeout(() => {
        if (!diagramRef.current) {
          console.error(
            "Timeout expired with diagram unmounted. Cannot process nodes"
          );
          return;
        }
        const container = d3.select<SVGGElement, unknown>(diagramRef.current);

        var nodeData = container.selectAll<SVGGElement, Node>(".node").data();
        var edgeData = container.selectAll<SVGGElement, Edge>(".edge").data();
        dataTarget.stream_expanded = false;
        nodeData.forEach((item) => {
          if (item.id === dataTarget.id) {
            item.stream_expanded = false;
          }
        });
        collapse(dataTarget);
        hideOrphanedEdge();
        refresh();

        // process all nodes
        function collapse(node: Node) {
          edgeData.forEach((edge) => {
            if (
              edge.recursive === false &&
              edge.target === node.id &&
              edge.hidden === dataTarget.stream_expanded
            ) {
              let s_node = getNode(nodeData, edge.source);
              edge.hidden = true; // to remove orphaned node -- hide checked edges.
              if (s_node && s_node.hidden === dataTarget.stream_expanded) {
                if (_getParentCount(edge.source) === 0) {
                  s_node.hidden = !dataTarget.stream_expanded;
                  if (s_node.type === "operation" || s_node.stream_expanded) {
                    collapse(s_node);
                  }
                }
              }
            }
          });
        }

        // hide / show all orphaned edges
        function hideOrphanedEdge() {
          edgeData.forEach((e) => {
            const sourceNode = getNode(nodeData, e.source);
            const targetNode = getNode(nodeData, e.target);
            e.hidden =
              (sourceNode && sourceNode.hidden) ||
              (targetNode && targetNode.hidden) ||
              (targetNode && !targetNode.stream_expanded);
          });
        }

        // check this node has only one parent
        function _getParentCount(id: string) {
          let count = 0;
          edgeData.forEach((e) => {
            if (e.source === id && !e.hidden) {
              count++;
            }
          });
          return count;
        }
      }, 0);
    });
  };

  // handle controller ( collapse / expand upstream)
  const handleUpstream = (event: React.MouseEvent<SVGGElement>, d: Node) => {
    event.stopPropagation();
    setTimeout(() => {
      if (!diagramRef.current) {
        console.error(
          "Timeout expired with diagram unmounted. Cannot process nodes"
        );
        return;
      }
      const container = d3.select<SVGGElement, unknown>(diagramRef.current);

      var nodeData = container.selectAll<SVGGElement, Node>(".node").data();
      var edgeData = container.selectAll<SVGGElement, Edge>(".edge").data();
      if (d.stream_expanded) {
        dispatch(setStreamID({ [d.id]: d }));
      } else {
        dispatch(delStreamID(d.id));
      }

      d.stream_expanded = !d.stream_expanded;
      collapse(d);
      hideOrphanedEdge();
      refresh();

      // process all nodes
      function collapse(node: Node) {
        edgeData.forEach((edge: Edge) => {
          if (
            edge.recursive === false &&
            edge.target === node.id &&
            edge.hidden === d.stream_expanded
          ) {
            let s_node = getNode(nodeData, edge.source);
            edge.hidden = true; // to remove orphaned node -- hide checked edges.
            if (s_node && s_node.hidden === d.stream_expanded) {
              if (_getParentCount(edge.source) === 0) {
                s_node.hidden = !d.stream_expanded;
                if (s_node.type === "operation" || s_node.stream_expanded) {
                  collapse(s_node);
                }
              }
            }
          }
        });
      }
      // hide / show all orphaned edges
      function hideOrphanedEdge() {
        edgeData.forEach((e) => {
          const sourceNode = getNode(nodeData, e.source);
          const targetNode = getNode(nodeData, e.target);
          e.hidden =
            (sourceNode && sourceNode.hidden) ||
            (targetNode && targetNode.hidden) ||
            (targetNode && !targetNode.stream_expanded);
        });
      }

      // check this node has only one parent
      function _getParentCount(id: string) {
        let count = 0;
        edgeData.forEach((e) => {
          if (e.source === id && !e.hidden) {
            count++;
          }
        });
        return count;
      }
    }, 100);
  };

  // refresh diagram(sync data and visible items)
  function refresh() {
    if (!diagramRef.current) {
      throw new Error("Cannot refresh diagram because diagram is not mounted");
    }
    const container = d3.select<SVGGElement, unknown>(diagramRef.current);
    container
      .selectAll<SVGGElement, Node>(".node")
      .data()
      .forEach((node) => {
        let nodeElem = document.getElementById(node.id);
        if (nodeElem) {
          nodeElem.setAttribute(
            "style",
            "display:" + (node.hidden ? "none" : "block")
          );
        }

        let handle = document.getElementById("handle_collapse_" + node.id);
        if (handle) {
          handle.setAttribute(
            "style",
            "display:" + (node.hidden ? "none" : "block")
          );
        }

        let tooltip = document.getElementById("notify_" + node.id);
        if (tooltip) {
          tooltip.setAttribute("style", "display:none");
        }
      });

    container
      .selectAll<SVGGElement, Edge>(".edge")
      .data()
      .forEach((edge) => {
        let edgeElem = document.getElementById(edge.id.toString());
        if (edgeElem) {
          edgeElem.setAttribute(
            "style",
            "display:" + (edge.hidden ? "none" : "block")
          );
        }
      });

    // refresh collapse handle.
    container
      .selectAll(".node .collapse-handle .arrow")
      .data(
        container
          .selectAll<SVGGElement, Node>(".node")
          .data()
          .filter((d) => canHaveCollapseHandle(d))
      )
      .attr("xlink:href", (d) => {
        return d.stream_expanded ? "#up_stream" : "#down_stream";
      });

    container
      .selectAll<SVGGElement, Node>(".node .collapse-handle .handle-connector")
      .data(
        container
          .selectAll<SVGGElement, Node>(".node")
          .data()
          .filter((d) => canHaveCollapseHandle(d))
      )
      .attr("class", (d) =>
        d.stream_expanded ? "handle-connector hidden" : "handle-connector"
      );

    // refresh recursive edges.
    container
      .selectAll<SVGGElement, Node>("g .recursive-edge")
      .data<Node>(
        container.selectAll<SVGGElement, Node>("g .recursive-edge").data()
      )
      .attr("class", (d: Node) => {
        let targetNode = getNode(
          container.selectAll<SVGGElement, Node>(".node").data(),
          d.recursiveCteTargetId || ""
        );
        return !d.hidden && targetNode && !targetNode.hidden
          ? "recursive-edge"
          : "recursive-edge hidden";
      });
  }

  // property handle controller ( show / hide detail view)
  const handleDetailView = (event: React.MouseEvent<SVGGElement>, d: Node) => {
    event.stopPropagation();
    if (!diagramRef.current) {
      throw new Error(
        "Cannot handleDetailView because the diagram is not mounted"
      );
    }
    const container = d3.select<SVGGElement, unknown>(diagramRef.current);

    let expand_IDs: string[] = [];
    container
      .selectAll<SVGGElement, Node>(".node")
      .data()
      .filter(
        (node) =>
          node.type === "dataset" &&
          d.columns !== undefined &&
          d.columns.length > 0
      )
      .forEach((node) => {
        if (node.id === d.id) node.collapse = !node.collapse;
        if (!node.collapse) {
          expand_IDs.push(node.id);
        }
      });

    batch(() => {
      dispatch(setUpdateFlag(true));
      dispatch(setExpandIDs(expand_IDs));
      dispatch(setZoomMode("none"));
      dispatch(setProduceDiagramFlag());
    });
  };

  const highlightInlineNodes = (d: Node) => {
    if (!diagramRef.current) {
      console.error("diagramRef.current not found. Cannot process nodes");
      return;
    }
    let ids: string[] = [];
    const container = d3.select<SVGGElement, unknown>(diagramRef.current);

    const nodeData = container.selectAll<SVGGElement, Node>(".node").data();
    const edgeData = container.selectAll<SVGGElement, Edge>(".edge").data();

    const getInlineNodesIds = (node: Node) => {
      edgeData.forEach((edge: Edge) => {
        if (edge.recursive === false && edge.target === node.id) {
          // s_node consist all nodes coming into that table
          const s_node = getNode(nodeData, edge.source);
          // get id of nodes with physical or logical subtype and push it into array to highlight
          if (d?.subtype?.toUpperCase() === "LOGICAL") {
            if (
              s_node?.subtype?.toUpperCase() === "PHYSICAL" ||
              s_node?.subtype?.toUpperCase() === "LOGICAL"
            ) {
              ids.push(s_node?.id);
            }
            if (s_node) {
              getInlineNodesIds(s_node);
            }
          } else if (d.type === "operation") {
            // get nodes with any tab coming to that operator
            if (s_node && s_node?.type?.toUpperCase() !== "OPERATION") {
              ids.push(s_node?.id);
            }
            // if parent node consist operation type then get nodes coming into it
            if (s_node && s_node.type === "operation") {
              getInlineNodesIds(s_node);
            }
          }
        }
      });
    };

    // Check if table is not highlighted
    getInlineNodesIds(d);
    // add id of table that which was clicked to highlight
    ids = [...ids, d.id];

    dispatch(setHighlightIDs({ [d.id]: ids }));
  };

  return (
    <>
      <svg className="diagram-svg" ref={svgRef}>
        <g id="mainDiagram" ref={diagramRef}></g>
      </svg>
      {chartData && Object.keys(chartData).length > 0 && (
        <>
          <ZoomToolNav />
          <div
            className={
              !actpropertyBoxState
                ? "float-toolbox flex-space"
                : "float-toolbox hidden-setting"
            }
          >
            <span
              title="Download"
              className={propertyBox ? "float-item1" : "float-item"}
              data-testid="download-diagram"
              onClick={() => {
                downloadSvgToPng();
              }}
            >
              <DownloadSvg />
            </span>
            {!actpropertyBoxState && (
              <span
                title="Show Settings"
                className="float-item"
                data-testid="show-settings"
                onClick={() => {
                  handleClickedShowProperyBox();
                }}
              >
                <SettingsSvg />
              </span>
            )}
          </div>
          <div className="zoom-notify-nav">
            <span
              title="Zoom Out"
              className="float-item"
              onClick={() => {
                handleClickedZoomOut();
              }}
            >
              <MinusSvg className="small-icon" />
            </span>
            <div className="scale-notify-box"></div>
            <span
              title="Zoom In"
              className="float-item"
              onClick={() => {
                handleClickedZoomIn();
              }}
            >
              <PlusSvg className="small-icon" />
            </span>
          </div>
        </>
      )}
    </>
  );
};

export default Diagram;
